// src/completionProvider.ts
import {
  CompletionItemKind
} from "vscode-languageserver";
import dlv6 from "dlv";

// src/util/isObject.ts
function isObject(variable) {
  return Object.prototype.toString.call(variable) === "[object Object]";
}

// src/util/removeMeta.ts
function removeMeta(obj) {
  let result = {};
  for (let key in obj) {
    if (key.substr(0, 2) === "__")
      continue;
    if (isObject(obj[key])) {
      result[key] = removeMeta(obj[key]);
    } else {
      result[key] = obj[key];
    }
  }
  return result;
}

// src/util/color.ts
import dlv2 from "dlv";

// src/util/rangesEqual.ts
function rangesEqual(a, b) {
  return a.start.line === b.start.line && a.start.character === b.start.character && a.end.line === b.end.line && a.end.character === b.end.character;
}

// src/util/array.ts
function dedupe(arr) {
  return arr.filter((value, index, self) => self.indexOf(value) === index);
}
function dedupeBy(arr, transform) {
  return arr.filter((value, index, self) => self.map(transform).indexOf(transform(value)) === index);
}
function dedupeByRange(arr) {
  return arr.filter(
    (classList, classListIndex) => classListIndex === arr.findIndex((c) => rangesEqual(c.range, classList.range))
  );
}
function ensureArray(value) {
  return Array.isArray(value) ? value : [value];
}
function flatten(arrays) {
  return [].concat.apply([], arrays);
}
function equal(a, b) {
  if (a === b)
    return true;
  if (a.length !== b.length)
    return false;
  let aSorted = a.concat().sort();
  let bSorted = b.concat().sort();
  for (let i = 0; i < aSorted.length; ++i) {
    if (aSorted[i] !== bSorted[i])
      return false;
  }
  return true;
}
function equalExact(a, b) {
  if (a === b)
    return true;
  if (a.length !== b.length)
    return false;
  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i])
      return false;
  }
  return true;
}

// src/util/combinations.ts
function combinations(str) {
  let fn = function(active, rest, a) {
    if (!active && !rest)
      return void 0;
    if (!rest) {
      a.push(active);
    } else {
      fn(active + rest[0], rest.slice(1), a);
      fn(active, rest.slice(1), a);
    }
    return a;
  };
  return fn("", str, []);
}

// src/util/getClassNameAtPosition.ts
import dlv from "dlv";
function getClassNameParts(state, className) {
  let separator = state.separator;
  className = className.replace(/^\./, "");
  let parts = className.split(separator);
  if (parts.length === 1) {
    return dlv(state.classNames.classNames, [className, "__info", "__rule"]) === true || Array.isArray(dlv(state.classNames.classNames, [className, "__info"])) ? [className] : null;
  }
  let points = combinations("123456789".substr(0, parts.length - 1)).map(
    (x) => x.split("").map((x2) => parseInt(x2, 10))
  );
  let possibilities = [
    [className],
    ...points.map((p) => {
      let result = [];
      let i = 0;
      p.forEach((x) => {
        result.push(parts.slice(i, x).join("-"));
        i = x;
      });
      result.push(parts.slice(i).join("-"));
      return result;
    })
  ];
  return possibilities.find((key) => {
    if (dlv(state.classNames.classNames, [...key, "__info", "__rule"]) === true || Array.isArray(dlv(state.classNames.classNames, [...key, "__info"]))) {
      return true;
    }
    return false;
  });
}

// src/util/pixelEquivalents.ts
import parseValue from "postcss-value-parser";
import { parse as parseMediaQueryList } from "@csstools/media-query-list-parser";
import { isTokenNode } from "@csstools/css-parser-algorithms";

// src/util/comments.ts
function applyComments(str, comments) {
  let offset = 0;
  for (let comment of comments) {
    let index = comment.index + offset;
    let commentStr = ` /* ${comment.value} */`;
    str = str.slice(0, index) + commentStr + str.slice(index);
    offset += commentStr.length;
  }
  return str;
}

// src/util/pixelEquivalents.ts
function addPixelEquivalentsToValue(value, rootFontSize) {
  if (!value.includes("rem")) {
    return value;
  }
  parseValue(value).walk((node) => {
    if (node.type !== "word") {
      return true;
    }
    let unit = parseValue.unit(node.value);
    if (!unit || unit.unit !== "rem") {
      return false;
    }
    let commentStr = ` /* ${parseFloat(unit.number) * rootFontSize}px */`;
    value = value.slice(0, node.sourceEndIndex) + commentStr + value.slice(node.sourceEndIndex);
    return false;
  });
  return value;
}
function getPixelEquivalentsForMediaQuery(params, rootFontSize) {
  let comments = [];
  try {
    parseMediaQueryList(params).forEach((mediaQuery) => {
      mediaQuery.walk(({ node }) => {
        if (isTokenNode(node) && node.type === "token" && node.value[0] === "dimension-token" && (node.value[4].type === "integer" || node.value[4].type === "number") && (node.value[4].unit === "rem" || node.value[4].unit === "em")) {
          comments.push({
            index: params.length - (params.length - node.value[3] - 1),
            value: `${node.value[4].value * rootFontSize}px`
          });
        }
      });
    });
  } catch {
  }
  return comments;
}
function addPixelEquivalentsToMediaQuery(query, rootFontSize) {
  return query.replace(/(?<=^\s*@media\s*).*?$/, (params) => {
    let comments = getPixelEquivalentsForMediaQuery(params, rootFontSize);
    return applyComments(params, comments);
  });
}
function equivalentPixelValues({
  comments,
  rootFontSize
}) {
  return {
    postcssPlugin: "plugin",
    AtRule: {
      media(atRule2) {
        if (!atRule2.params.includes("em")) {
          return;
        }
        comments.push(
          ...getPixelEquivalentsForMediaQuery(atRule2.params, rootFontSize).map(
            ({ index, value }) => ({
              index: index + atRule2.source.start.offset + `@media${atRule2.raws.afterName}`.length,
              value
            })
          )
        );
      }
    },
    Declaration(decl2) {
      if (!decl2.value.includes("rem")) {
        return;
      }
      parseValue(decl2.value).walk((node) => {
        if (node.type !== "word") {
          return true;
        }
        let unit = parseValue.unit(node.value);
        if (!unit || unit.unit !== "rem") {
          return false;
        }
        comments.push({
          index: decl2.source.start.offset + `${decl2.prop}${decl2.raws.between}`.length + node.sourceEndIndex,
          value: `${parseFloat(unit.number) * rootFontSize}px`
        });
        return false;
      });
    }
  };
}
equivalentPixelValues.postcss = true;

// src/util/colorEquivalents.ts
import parseValue2 from "postcss-value-parser";
import { inGamut } from "culori";
var allowedFunctions = ["rgb", "rgba", "hsl", "hsla", "lch", "lab", "oklch", "oklab"];
function equivalentColorValues({ comments }) {
  return {
    postcssPlugin: "plugin",
    Declaration(decl2) {
      if (!allowedFunctions.some((fn) => decl2.value.includes(fn))) {
        return;
      }
      parseValue2(decl2.value).walk((node) => {
        if (node.type !== "function") {
          return true;
        }
        if (!allowedFunctions.includes(node.value)) {
          return false;
        }
        const values = node.nodes.filter((n) => n.type === "word").map((n) => n.value);
        if (values.length < 3) {
          return false;
        }
        const color = getColorFromValue(`${node.value}(${values.join(" ")})`);
        if (!inGamut("rgb")(color)) {
          return false;
        }
        if (!color || typeof color === "string") {
          return false;
        }
        comments.push({
          index: decl2.source.start.offset + `${decl2.prop}${decl2.raws.between}`.length + node.sourceEndIndex,
          value: formatColor(color)
        });
        return false;
      });
    }
  };
}
equivalentColorValues.postcss = true;

// src/util/equivalents.ts
import postcss from "postcss";
function addEquivalents(css, settings) {
  let comments = [];
  let plugins = [];
  if (settings.showPixelEquivalents) {
    plugins.push(
      equivalentPixelValues({
        comments,
        rootFontSize: settings.rootFontSize
      })
    );
  }
  plugins.push(equivalentColorValues({ comments }));
  try {
    postcss(plugins).process(css, { from: void 0 }).css;
  } catch {
    return css;
  }
  return applyComments(css, comments);
}

// src/util/jit.ts
function bigSign(bigIntValue) {
  return (bigIntValue > 0n) - (bigIntValue < 0n);
}
function generateRules(state, classNames, filter = () => true) {
  let rules = state.modules.jit.generateRules.module(new Set(classNames), state.jitContext).sort(([a], [z]) => bigSign(a - z));
  let root2 = state.modules.postcss.module.root({ nodes: rules.map(([, rule2]) => rule2) });
  state.modules.jit.expandApplyAtRules.module(state.jitContext)(root2);
  state.modules.jit.evaluateTailwindFunctions?.module?.(state.jitContext)(root2);
  let actualRules = [];
  root2.walkRules((subRule) => {
    if (filter(subRule)) {
      actualRules.push(subRule);
    }
  });
  return {
    root: root2,
    rules: actualRules
  };
}
async function stringifyRoot(state, root2, uri) {
  let settings = await state.editor.getConfiguration(uri);
  let clone = root2.clone();
  clone.walkAtRules("defaults", (node) => {
    node.remove();
  });
  let css = clone.toString();
  css = addEquivalents(css, settings.tailwindCSS);
  let identSize = state.v4 ? 2 : 4;
  let identPattern = state.v4 ? /^(?:  )+/gm : /^(?:    )+/gm;
  return css.replace(/([^;{}\s])(\n\s*})/g, (_match, before, after) => `${before};${after}`).replace(
    identPattern,
    (indent) => " ".repeat(indent.length / identSize * settings.editor.tabSize)
  );
}
async function stringifyDecls(state, rule2, uri) {
  let settings = await state.editor.getConfiguration(uri);
  let result = [];
  rule2.walkDecls(({ prop, value }) => {
    if (settings.tailwindCSS.showPixelEquivalents) {
      value = addPixelEquivalentsToValue(value, settings.tailwindCSS.rootFontSize);
    }
    result.push(`${prop}: ${value};`);
  });
  return result.join(" ");
}
function replaceClassName(state, selector, find, replace2) {
  const transform = (selectors) => {
    selectors.walkClasses((className) => {
      if (className.value === find) {
        className.value = replace2;
      }
    });
  };
  return state.modules.postcssSelectorParser.module(transform).processSync(selector);
}
function isAtRule(node) {
  return node.type === "atrule";
}
function getRuleContext(state, rule2, className) {
  let context = [replaceClassName(state, rule2.selector, className, "__placeholder__")];
  let p = rule2;
  while (p.parent && p.parent.type !== "root") {
    p = p.parent;
    if (isAtRule(p)) {
      context.unshift(`@${p.name} ${p.params}`);
    }
  }
  return context;
}

// src/util/color.ts
import * as culori from "culori";
import namedColors from "color-name";
import postcss2 from "postcss";
var COLOR_PROPS = [
  "accent-color",
  "caret-color",
  "color",
  "column-rule-color",
  "background-color",
  "border-color",
  "border-top-color",
  "border-right-color",
  "border-bottom-color",
  "border-left-color",
  "fill",
  "outline-color",
  "stop-color",
  "stroke",
  "text-decoration-color"
];
function getKeywordColor(value) {
  if (typeof value !== "string")
    return null;
  let lowercased = value.toLowerCase();
  if (lowercased === "transparent") {
    return "transparent";
  }
  if (lowercased === "currentcolor") {
    return "currentColor";
  }
  return null;
}
var colorRegex = new RegExp(
  `(?:^|\\s|\\(|,)(#(?:[0-9a-f]{2}){2,4}|(#[0-9a-f]{3})|(rgba?|hsla?|(?:ok)?(?:lab|lch))\\(\\s*(-?[\\d.]+%?(\\s*[,/]\\s*|\\s+)+){2,3}\\s*([\\d.]+%?|var\\([^)]+\\))?\\)|transparent|currentColor|${Object.keys(
    namedColors
  ).join("|")})(?:$|\\s|\\)|,)`,
  "gi"
);
function replaceColorVarsWithTheirDefaults(str) {
  return str.replace(/((?:rgba?|hsla?|(?:ok)?(?:lab|lch))\(\s*)var\([^,]+,\s*([^)]+)\)/gi, "$1$2");
}
function replaceHexColorVarsWithTheirDefaults(str) {
  return str.replace(/var\([^,]+,\s*(#[^)]+)\)/gi, "$1");
}
function getColorsInString(str) {
  if (/(?:box|drop)-shadow/.test(str))
    return [];
  function toColor(match) {
    let color = match[1].replace(/var\([^)]+\)/, "1");
    return getKeywordColor(color) ?? culori.parse(color);
  }
  str = replaceHexColorVarsWithTheirDefaults(str);
  str = replaceColorVarsWithTheirDefaults(str);
  str = removeColorMixWherePossible(str);
  let possibleColors = str.matchAll(colorRegex);
  return Array.from(possibleColors, toColor).filter(Boolean);
}
function getColorFromDecls(decls) {
  let props = Object.keys(decls).filter((prop) => {
    if (prop === "content" && (decls[prop] === '""' || decls[prop] === "''" || decls[prop] === "var(--tw-content)")) {
      return false;
    }
    return true;
  });
  if (props.length === 0)
    return null;
  const nonCustomProps = props.filter((prop) => !prop.startsWith("--"));
  const areAllCustom = nonCustomProps.length === 0;
  if (!areAllCustom && nonCustomProps.some((prop) => !COLOR_PROPS.includes(prop))) {
    return null;
  }
  const propsToCheck = areAllCustom ? props : nonCustomProps;
  const colors = propsToCheck.flatMap((prop) => ensureArray(decls[prop]).flatMap(getColorsInString));
  const colorStrings = dedupe(
    colors.map(
      (color) => typeof color === "string" ? color : culori.formatRgb({ ...color, alpha: void 0 })
    )
  );
  if (colorStrings.length !== 1) {
    return null;
  }
  let keyword = getKeywordColor(colorStrings[0]);
  if (keyword) {
    return keyword;
  }
  const nonKeywordColors = colors.filter(
    (color) => typeof color !== "string"
  );
  const alphas = dedupe(nonKeywordColors.map((color) => color.alpha ?? 1));
  if (alphas.length === 1) {
    return nonKeywordColors[0];
  }
  if (alphas.length === 2 && alphas.includes(0)) {
    return nonKeywordColors.find((color) => (color.alpha ?? 1) !== 0);
  }
  return null;
}
function getColorFromRoot(state, css) {
  css = css.clone();
  css.walkAtRules((rule3) => {
    if (rule3.name === "property") {
      rule3.remove();
    }
    if (rule3.name === "supports" && rule3.params === "(-moz-orient: inline)") {
      rule3.remove();
    }
  });
  let decls = {};
  let rule2 = postcss2.rule({
    selector: ".x",
    nodes: []
  });
  css.walkDecls((decl2) => {
    rule2.append(decl2.clone());
  });
  css.walkDecls((decl2) => {
    decls[decl2.prop] ??= [];
    decls[decl2.prop].push(decl2.value);
  });
  return getColorFromDecls(decls);
}
function getColor(state, className) {
  if (state.v4) {
    let css = state.designSystem.compile([className])[0];
    let color = getColorFromRoot(state, css);
    return color;
  }
  if (state.jit) {
    if (state.classNames) {
      const item2 = dlv2(state.classNames.classNames, [className, "__info"]);
      if (item2 && item2.__rule) {
        return getColorFromDecls(removeMeta(item2));
      }
    }
    let result;
    try {
      result = generateRules(state, [className]);
    } catch (err) {
      console.error(`Error generating rules for className: ${className}`);
      console.error(err);
      return null;
    }
    let { root: root2, rules } = result;
    if (rules.length === 0)
      return null;
    let decls = {};
    root2.walkDecls((decl2) => {
      let value = decls[decl2.prop];
      if (value) {
        if (Array.isArray(value)) {
          value.push(decl2.value);
        } else {
          decls[decl2.prop] = [value, decl2.value];
        }
      } else {
        decls[decl2.prop] = decl2.value;
      }
    });
    return getColorFromDecls(decls);
  }
  let parts = getClassNameParts(state, className);
  if (!parts)
    return null;
  const item = dlv2(state.classNames.classNames, [...parts, "__info"]);
  if (!item.__rule)
    return null;
  return getColorFromDecls(removeMeta(item));
}
function getColorFromValue(value) {
  if (typeof value !== "string")
    return null;
  const trimmedValue = value.trim();
  if (trimmedValue.toLowerCase() === "transparent") {
    return "transparent";
  }
  if (trimmedValue.toLowerCase() === "currentcolor") {
    return "currentColor";
  }
  if (!/^\s*(?:rgba?|hsla?|(?:ok)?(?:lab|lch))\s*\([^)]+\)\s*$/.test(trimmedValue) && !/^\s*#[0-9a-f]+\s*$/i.test(trimmedValue) && !Object.keys(namedColors).includes(trimmedValue)) {
    return null;
  }
  const color = culori.parse(trimmedValue);
  return color ?? null;
}
var toRgb = culori.converter("rgb");
function culoriColorToVscodeColor(color) {
  let rgb = culori.clampRgb(toRgb(color));
  return { red: rgb.r, green: rgb.g, blue: rgb.b, alpha: rgb.alpha ?? 1 };
}
function formatColor(color) {
  if (color.alpha === void 0 || color.alpha === 1) {
    return culori.formatHex(color);
  }
  return culori.formatHex8(color);
}
var COLOR_MIX_REGEX = /color-mix\(in srgb, (.*?) (\d+|\.\d+|\d+\.\d+)%, transparent\)/g;
function removeColorMixWherePossible(str) {
  return str.replace(COLOR_MIX_REGEX, (match, color, percentage) => {
    if (color.startsWith("var("))
      return match;
    let parsed = culori.parse(color);
    if (!parsed)
      return match;
    let alpha = Number(percentage) / 100;
    if (Number.isNaN(alpha))
      return match;
    return culori.formatRgb({ ...parsed, alpha });
  });
}

// src/util/languages.ts
var htmlLanguages = [
  "aspnetcorerazor",
  "astro",
  "astro-markdown",
  "blade",
  "django-html",
  "edge",
  "ejs",
  "erb",
  "gohtml",
  "GoHTML",
  "gohtmltmpl",
  "haml",
  "handlebars",
  "hbs",
  "html",
  "HTML (Eex)",
  "HTML (EEx)",
  "html-eex",
  "htmldjango",
  "jade",
  "leaf",
  "liquid",
  "markdown",
  "mdx",
  "mustache",
  "njk",
  "nunjucks",
  "phoenix-heex",
  "php",
  "razor",
  "slim",
  "surface",
  "twig"
];
var cssLanguages = [
  "css",
  "less",
  "postcss",
  "sass",
  "scss",
  "stylus",
  "sugarss",
  "tailwindcss"
];
var jsLanguages = [
  "javascript",
  "javascriptreact",
  "reason",
  "rescript",
  "typescript",
  "typescriptreact",
  "glimmer-js",
  "glimmer-ts"
];
var specialLanguages = ["vue", "svelte"];
var languages = [...cssLanguages, ...htmlLanguages, ...jsLanguages, ...specialLanguages];
var semicolonlessLanguages = ["sass", "sugarss", "stylus"];
function isSemicolonlessCssLanguage(languageId, userLanguages = {}) {
  return semicolonlessLanguages.includes(languageId) || semicolonlessLanguages.includes(userLanguages[languageId]);
}

// src/util/find.ts
import lineColumn from "line-column";

// src/util/js.ts
function isJsDoc(state, doc) {
  const userJsLanguages = Object.keys(state.editor.userLanguages).filter(
    (lang) => jsLanguages.includes(state.editor.userLanguages[lang])
  );
  return [...jsLanguages, ...userJsLanguages].indexOf(doc.languageId) !== -1;
}
function isJsxContext(state, doc, position) {
  let str = doc.getText({
    start: { line: 0, character: 0 },
    end: position
  });
  let boundaries = getLanguageBoundaries(state, doc, str);
  return boundaries ? ["jsx", "tsx"].includes(boundaries[boundaries.length - 1].type) : false;
}

// src/util/css.ts
function getCssLanguages(state) {
  const userCssLanguages = Object.keys(state.editor.userLanguages).filter(
    (lang) => cssLanguages.includes(state.editor.userLanguages[lang])
  );
  return [...cssLanguages, ...userCssLanguages];
}
function isCssLanguage(state, lang) {
  return getCssLanguages(state).indexOf(lang) !== -1;
}
function isCssDoc(state, doc) {
  return isCssLanguage(state, doc.languageId);
}
function isCssContext(state, doc, position) {
  if (isCssDoc(state, doc)) {
    return true;
  }
  if (isHtmlDoc(state, doc) || isVueDoc(doc) || isSvelteDoc(doc) || isJsDoc(state, doc)) {
    let str = doc.getText({
      start: { line: 0, character: 0 },
      end: position
    });
    let boundaries = getLanguageBoundaries(state, doc, str);
    return boundaries ? boundaries[boundaries.length - 1].type === "css" : false;
  }
  return false;
}

// src/util/isWithinRange.ts
function isWithinRange(position, range) {
  if (position.line === range.start.line && position.character >= range.start.character) {
    if (position.line === range.end.line && position.character > range.end.character) {
      return false;
    } else {
      return true;
    }
  }
  if (position.line === range.end.line && position.character <= range.end.character) {
    if (position.line === range.start.line && position.character < range.end.character) {
      return false;
    } else {
      return true;
    }
  }
  if (position.line > range.start.line && position.line < range.end.line) {
    return true;
  }
  return false;
}

// src/util/lexers.ts
import moo from "moo";

// src/util/lazy.ts
var lazy = (getter) => {
  let evaluated = false;
  let _res = null;
  const res = function() {
    if (evaluated)
      return _res;
    _res = getter.apply(this, arguments);
    evaluated = true;
    return _res;
  };
  res.isLazy = true;
  return res;
};

// src/util/lexers.ts
var classAttributeStates = () => ({
  doubleClassList: {
    arb: { match: new RegExp("(?<!\\\\)\\["), push: "arbitrary" },
    lbrace: { match: new RegExp("(?<!\\\\)\\{"), push: "interpBrace" },
    rbrace: { match: new RegExp("(?<!\\\\)\\}"), pop: 1 },
    end: { match: new RegExp('(?<!\\\\)"'), pop: 1 },
    classlist: { match: new RegExp("[\\s\\S]"), lineBreaks: true }
  },
  singleClassList: {
    lbrace: { match: new RegExp("(?<!\\\\)\\{"), push: "interpBrace" },
    rbrace: { match: new RegExp("(?<!\\\\)\\}"), pop: 1 },
    end: { match: new RegExp("(?<!\\\\)'"), pop: 1 },
    classlist: { match: new RegExp("[\\s\\S]"), lineBreaks: true }
  },
  tickClassList: {
    lbrace: { match: new RegExp("(?<=(?<!\\\\)\\$)\\{"), push: "interpBrace" },
    rbrace: { match: new RegExp("(?<!\\\\)\\}"), pop: 1 },
    end: { match: new RegExp("(?<!\\\\)`"), pop: 1 },
    classlist: { match: new RegExp("[\\s\\S]"), lineBreaks: true }
  },
  interpBrace: {
    startSingle: { match: new RegExp("(?<!\\\\)'"), push: "singleClassList" },
    startDouble: { match: new RegExp('(?<!\\\\)"'), push: "doubleClassList" },
    startTick: { match: new RegExp("(?<!\\\\)`"), push: "tickClassList" },
    lbrace: { match: new RegExp("(?<!\\\\)\\{"), push: "interpBrace" },
    rbrace: { match: new RegExp("(?<!\\\\)\\}"), pop: 1 },
    text: { match: new RegExp("[\\s\\S]"), lineBreaks: true }
  },
  interpSingle: {
    startDouble: { match: new RegExp('(?<!\\\\)"'), push: "doubleClassList" },
    startTick: { match: new RegExp("(?<!\\\\)`"), push: "tickClassList" },
    single: { match: new RegExp("(?<!\\\\)'"), pop: 1 },
    text: { match: new RegExp("[\\s\\S]"), lineBreaks: true }
  },
  interpDouble: {
    startSingle: { match: new RegExp("(?<!\\\\)'"), push: "singleClassList" },
    startTick: { match: new RegExp("(?<!\\\\)`"), push: "tickClassList" },
    double: { match: new RegExp('(?<!\\\\)"'), pop: 1 },
    text: { match: new RegExp("[\\s\\S]"), lineBreaks: true }
  },
  arbitrary: {
    arb: { match: new RegExp("(?<!\\\\)\\]"), pop: 1 },
    space: { match: /\s/, pop: 1, lineBreaks: true },
    arb2: { match: new RegExp("[\\s\\S]"), lineBreaks: true }
  }
});
var simpleClassAttributeStates = {
  main: {
    start: { match: '"', push: "doubleClassList" }
  },
  doubleClassList: {
    end: { match: '"', pop: 1 },
    classlist: { match: /[\s\S]/, lineBreaks: true }
  }
};
var getClassAttributeLexer = lazy(() => {
  let supportsNegativeLookbehind = true;
  try {
    new RegExp("(?<!)");
  } catch (_) {
    supportsNegativeLookbehind = false;
  }
  if (supportsNegativeLookbehind) {
    return moo.states({
      main: {
        start1: { match: '"', push: "doubleClassList" },
        start2: { match: "'", push: "singleClassList" },
        start3: { match: "{", push: "interpBrace" }
      },
      ...classAttributeStates()
    });
  }
  return moo.states(simpleClassAttributeStates);
});
var getComputedClassAttributeLexer = lazy(() => {
  let supportsNegativeLookbehind = true;
  try {
    new RegExp("(?<!)");
  } catch (_) {
    supportsNegativeLookbehind = false;
  }
  if (supportsNegativeLookbehind) {
    return moo.states({
      main: {
        lbrace: { match: "{", push: "interpBrace" },
        single: { match: "'", push: "interpSingle" },
        double: { match: '"', push: "interpDouble" }
      },
      ...classAttributeStates()
    });
  }
  return moo.states(simpleClassAttributeStates);
});

// src/util/resolveRange.ts
function resolveRange(range, relativeTo) {
  return {
    start: {
      line: (relativeTo?.start.line || 0) + range.start.line,
      character: (range.end.line === 0 ? relativeTo?.start.character || 0 : 0) + range.start.character
    },
    end: {
      line: (relativeTo?.start.line || 0) + range.end.line,
      character: (range.end.line === 0 ? relativeTo?.start.character || 0 : 0) + range.end.character
    }
  };
}

// src/util/doc.ts
import moo2 from "moo";
function getTextWithoutComments(docOrText, type, range) {
  let text2 = typeof docOrText === "string" ? docOrText : docOrText.getText(range);
  if (type === "js" || type === "jsx") {
    return getJsWithoutComments(text2);
  }
  if (type === "css") {
    return text2.replace(/\/\*.*?\*\//gs, replace);
  }
  return text2.replace(/<!--.*?-->/gs, replace);
}
function replace(match) {
  return match.replace(/./gs, (char) => char === "\n" ? "\n" : " ");
}
var jsLexer;
function getJsWithoutComments(text2) {
  if (!jsLexer) {
    jsLexer = moo2.states({
      main: {
        commentLine: /\/\/.*?$/,
        commentBlock: { match: /\/\*[^]*?\*\//, lineBreaks: true },
        stringDouble: /"(?:[^"\\]|\\.)*"/,
        stringSingle: /'(?:[^'\\]|\\.)*'/,
        stringBacktick: /`(?:[^`\\]|\\.)*`/,
        other: { match: /[^]/, lineBreaks: true }
      }
    });
  }
  let str = "";
  jsLexer.reset(text2);
  for (let token of jsLexer) {
    if (token.type === "commentLine") {
      str += " ".repeat(token.value.length);
    } else if (token.type === "commentBlock") {
      str += token.value.replace(/./g, " ");
    } else {
      str += token.value;
    }
  }
  return str;
}

// src/util/classes.ts
function* customClassesIn({
  text: text2,
  filters,
  cursor = null
}) {
  for (let filter of filters) {
    let [containerPattern, classPattern] = Array.isArray(filter) ? filter : [filter];
    let containerRegex = new RegExp(containerPattern, "gd");
    let classRegex = classPattern ? new RegExp(classPattern, "gd") : void 0;
    for (let match of matchesIn(text2, containerRegex, classRegex, cursor)) {
      yield match;
    }
  }
}
function* matchesIn(text2, containerRegex, classRegex, cursor) {
  for (let containerMatch of text2.matchAll(containerRegex)) {
    if (containerMatch[1] === void 0) {
      console.warn(`Regex /${containerRegex.source}/ must have exactly one capture group`);
      continue;
    }
    const matchStart = containerMatch.indices[1][0];
    const matchEnd = matchStart + containerMatch[1].length;
    if (cursor !== null && (cursor < matchStart || cursor > matchEnd)) {
      continue;
    }
    if (!classRegex) {
      yield {
        classList: cursor !== null ? containerMatch[1].slice(0, cursor - matchStart) : containerMatch[1],
        range: [matchStart, matchEnd]
      };
      continue;
    }
    for (let classMatch of containerMatch[1].matchAll(classRegex)) {
      if (classMatch[1] === void 0) {
        console.warn(`Regex /${classRegex.source}/ must have exactly one capture group`);
        continue;
      }
      const classMatchStart = matchStart + classMatch.indices[1][0];
      const classMatchEnd = classMatchStart + classMatch[1].length;
      if (cursor !== null && (cursor < classMatchStart || cursor > classMatchEnd)) {
        continue;
      }
      yield {
        classList: cursor !== null ? classMatch[1].slice(0, cursor - classMatchStart) : classMatch[1],
        range: [classMatchStart, classMatchEnd]
      };
    }
  }
}

// src/util/find.ts
function findAll(re, str) {
  let match;
  let matches = [];
  while ((match = re.exec(str)) !== null) {
    matches.push({ ...match });
  }
  return matches;
}
function findLast(re, str) {
  const matches = findAll(re, str);
  if (matches.length === 0) {
    return null;
  }
  return matches[matches.length - 1];
}
function getClassNamesInClassList({ classList, range, important }, blocklist) {
  const parts = classList.split(/(\s+)/);
  const names = [];
  let index = 0;
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0 && !blocklist.includes(parts[i])) {
      const start = indexToPosition(classList, index);
      const end = indexToPosition(classList, index + parts[i].length);
      names.push({
        className: parts[i],
        classList: {
          classList,
          range,
          important
        },
        relativeRange: {
          start,
          end
        },
        range: {
          start: {
            line: range.start.line + start.line,
            character: (end.line === 0 ? range.start.character : 0) + start.character
          },
          end: {
            line: range.start.line + end.line,
            character: (end.line === 0 ? range.start.character : 0) + end.character
          }
        }
      });
    }
    index += parts[i].length;
  }
  return names;
}
async function findClassNamesInRange(state, doc, range, mode, includeCustom = true) {
  const classLists = await findClassListsInRange(state, doc, range, mode, includeCustom);
  return flatten(
    classLists.map((classList) => getClassNamesInClassList(classList, state.blocklist))
  );
}
function findClassListsInCssRange(state, doc, range, lang) {
  const text2 = getTextWithoutComments(doc, "css", range);
  let regex = isSemicolonlessCssLanguage(lang ?? doc.languageId, state.editor?.userLanguages) ? /(@apply\s+)(?<classList>[^}\r\n]+?)(?<important>\s*!important)?(?:\r|\n|}|$)/g : /(@apply\s+)(?<classList>[^;}]+?)(?<important>\s*!important)?\s*[;}]/g;
  const matches = findAll(regex, text2);
  const globalStart = range ? range.start : { line: 0, character: 0 };
  return matches.map((match) => {
    const start = indexToPosition(text2, match.index + match[1].length);
    const end = indexToPosition(text2, match.index + match[1].length + match.groups.classList.length);
    return {
      classList: match.groups.classList,
      important: Boolean(match.groups.important),
      range: {
        start: {
          line: globalStart.line + start.line,
          character: (end.line === 0 ? globalStart.character : 0) + start.character
        },
        end: {
          line: globalStart.line + end.line,
          character: (end.line === 0 ? globalStart.character : 0) + end.character
        }
      }
    };
  });
}
async function findCustomClassLists(state, doc, range) {
  const settings = await state.editor.getConfiguration(doc.uri);
  const regexes = settings.tailwindCSS.experimental.classRegex;
  if (!Array.isArray(regexes) || regexes.length === 0)
    return [];
  const text2 = doc.getText(range ? { ...range, start: doc.positionAt(0) } : void 0);
  const result = [];
  try {
    for (let match of customClassesIn({ text: text2, filters: regexes })) {
      result.push({
        classList: match.classList,
        range: {
          start: doc.positionAt(match.range[0]),
          end: doc.positionAt(match.range[1])
        }
      });
    }
  } catch (err) {
    console.log(JSON.stringify({ text: text2, filters: regexes }));
    throw new Error("Failed to parse custom class regex");
  }
  return result;
}
function matchClassAttributes(text2, attributes) {
  const attrs = attributes.filter((x) => typeof x === "string").flatMap((a) => [a, `\\[${a}\\]`]);
  const re = /(?:\s|:|\()(ATTRS)\s*=\s*['"`{]/;
  return findAll(new RegExp(re.source.replace("ATTRS", attrs.join("|")), "gi"), text2);
}
async function findClassListsInHtmlRange(state, doc, type, range) {
  const text2 = getTextWithoutComments(doc, type, range);
  const matches = matchClassAttributes(
    text2,
    (await state.editor.getConfiguration(doc.uri)).tailwindCSS.classAttributes
  );
  const result = [];
  matches.forEach((match) => {
    const subtext = text2.substr(match.index + match[0].length - 1);
    let lexer = match[0][0] === ":" || match[1].startsWith("[") && match[1].endsWith("]") ? getComputedClassAttributeLexer() : getClassAttributeLexer();
    lexer.reset(subtext);
    let classLists = [];
    let token;
    let currentClassList;
    try {
      for (let token2 of lexer) {
        if (token2.type === "classlist" || token2.type.startsWith("arb")) {
          if (currentClassList) {
            currentClassList.value += token2.value;
          } else {
            currentClassList = {
              value: token2.value,
              offset: token2.offset
            };
          }
        } else {
          if (currentClassList) {
            classLists.push({
              value: currentClassList.value,
              offset: currentClassList.offset
            });
          }
          currentClassList = void 0;
        }
      }
    } catch (_) {
    }
    if (currentClassList) {
      classLists.push({
        value: currentClassList.value,
        offset: currentClassList.offset
      });
    }
    result.push(
      ...classLists.map(({ value, offset }) => {
        if (value.trim() === "") {
          return null;
        }
        const before = value.match(/^\s*/);
        const beforeOffset = before === null ? 0 : before[0].length;
        const after = value.match(/\s*$/);
        const afterOffset = after === null ? 0 : -after[0].length;
        const start = indexToPosition(
          text2,
          match.index + match[0].length - 1 + offset + beforeOffset
        );
        const end = indexToPosition(
          text2,
          match.index + match[0].length - 1 + offset + value.length + afterOffset
        );
        return {
          classList: value.substr(beforeOffset, value.length + afterOffset),
          range: {
            start: {
              line: (range?.start.line || 0) + start.line,
              character: (end.line === 0 ? range?.start.character || 0 : 0) + start.character
            },
            end: {
              line: (range?.start.line || 0) + end.line,
              character: (end.line === 0 ? range?.start.character || 0 : 0) + end.character
            }
          }
        };
      }).filter((x) => x !== null)
    );
  });
  return result;
}
async function findClassListsInRange(state, doc, range, mode, includeCustom = true) {
  let classLists = [];
  if (mode === "css") {
    classLists = findClassListsInCssRange(state, doc, range);
  } else if (mode === "html" || mode === "jsx") {
    classLists = await findClassListsInHtmlRange(state, doc, mode, range);
  }
  return dedupeByRange([
    ...classLists,
    ...includeCustom ? await findCustomClassLists(state, doc, range) : []
  ]);
}
async function findClassListsInDocument(state, doc) {
  if (isCssDoc(state, doc)) {
    return findClassListsInCssRange(state, doc);
  }
  let boundaries = getLanguageBoundaries(state, doc);
  if (!boundaries)
    return [];
  return dedupeByRange(
    flatten([
      ...await Promise.all(
        boundaries.filter((b) => b.type === "html" || b.type === "jsx").map(
          ({ type, range }) => findClassListsInHtmlRange(state, doc, type === "html" ? "html" : "jsx", range)
        )
      ),
      ...boundaries.filter((b) => b.type === "css").map(({ range, lang }) => findClassListsInCssRange(state, doc, range, lang)),
      await findCustomClassLists(state, doc)
    ])
  );
}
function findHelperFunctionsInDocument(state, doc) {
  if (isCssDoc(state, doc)) {
    return findHelperFunctionsInRange(doc);
  }
  let boundaries = getLanguageBoundaries(state, doc);
  if (!boundaries)
    return [];
  return flatten(
    boundaries.filter((b) => b.type === "css").map(({ range }) => findHelperFunctionsInRange(doc, range))
  );
}
function getFirstCommaIndex(str) {
  let quoteChar;
  for (let i = 0; i < str.length; i++) {
    let char = str[i];
    if (char === "," && !quoteChar) {
      return i;
    }
    if (!quoteChar && (char === '"' || char === "'")) {
      quoteChar = char;
    } else if (char === quoteChar) {
      quoteChar = void 0;
    }
  }
  return null;
}
function findHelperFunctionsInRange(doc, range) {
  const text2 = getTextWithoutComments(doc, "css", range);
  let matches = findAll(
    /(?<prefix>[\s:;/*(){}])(?<helper>config|theme)(?<innerPrefix>\(\s*)(?<path>[^)]*?)\s*\)/g,
    text2
  );
  return matches.map((match) => {
    let quotesBefore = "";
    let path = match.groups.path;
    let commaIndex = getFirstCommaIndex(path);
    if (commaIndex !== null) {
      path = path.slice(0, commaIndex).trimEnd();
    }
    path = path.replace(/['"]+$/, "").replace(/^['"]+/, (m) => {
      quotesBefore = m;
      return "";
    });
    let matches2 = path.match(/^([^\s]+)(?![^\[]*\])(?:\s*\/\s*([^\/\s]+))$/);
    if (matches2) {
      path = matches2[1];
    }
    path = path.replace(/['"]*\s*$/, "");
    let startIndex = match.index + match.groups.prefix.length + match.groups.helper.length + match.groups.innerPrefix.length;
    return {
      helper: match.groups.helper === "theme" ? "theme" : "config",
      path,
      ranges: {
        full: resolveRange(
          {
            start: indexToPosition(text2, startIndex),
            end: indexToPosition(text2, startIndex + match.groups.path.length)
          },
          range
        ),
        path: resolveRange(
          {
            start: indexToPosition(text2, startIndex + quotesBefore.length),
            end: indexToPosition(text2, startIndex + quotesBefore.length + path.length)
          },
          range
        )
      }
    };
  });
}
function indexToPosition(str, index) {
  const { line, col } = lineColumn(str + "\n").fromIndex(index) ?? { line: 1, col: 1 };
  return { line: line - 1, character: col - 1 };
}
async function findClassNameAtPosition(state, doc, position) {
  let classNames = [];
  const positionOffset = doc.offsetAt(position);
  const searchRange = {
    start: doc.positionAt(Math.max(0, positionOffset - 2e3)),
    end: doc.positionAt(positionOffset + 2e3)
  };
  if (isVueDoc(doc)) {
    let boundaries = getLanguageBoundaries(state, doc);
    let groups = await Promise.all(
      boundaries.map(async ({ type, range, lang }) => {
        if (type === "css") {
          return findClassListsInCssRange(state, doc, range, lang);
        }
        if (type === "html") {
          return await findClassListsInHtmlRange(state, doc, "html", range);
        }
        if (type === "jsx") {
          return await findClassListsInHtmlRange(state, doc, "jsx", range);
        }
        return [];
      })
    );
    classNames = dedupeByRange(flatten(groups)).flatMap(
      (classList) => getClassNamesInClassList(classList, state.blocklist)
    );
  } else if (isCssContext(state, doc, position)) {
    classNames = await findClassNamesInRange(state, doc, searchRange, "css");
  } else if (isHtmlContext(state, doc, position)) {
    classNames = await findClassNamesInRange(state, doc, searchRange, "html");
  } else if (isJsxContext(state, doc, position)) {
    classNames = await findClassNamesInRange(state, doc, searchRange, "jsx");
  } else {
    classNames = await findClassNamesInRange(state, doc, searchRange);
  }
  if (classNames.length === 0) {
    return null;
  }
  const className = classNames.find(({ range }) => isWithinRange(position, range));
  if (!className)
    return null;
  return className;
}

// src/util/getLanguageBoundaries.ts
import moo3 from "moo";
import Cache from "tmp-cache";
var htmlScriptTypes = [
  // https://v3-migration.vuejs.org/breaking-changes/inline-template-attribute.html#option-1-use-script-tag
  "text/html",
  // https://vuejs.org/guide/essentials/component-basics.html#dom-template-parsing-caveats
  "text/x-template",
  // https://github.com/tailwindlabs/tailwindcss-intellisense/issues/722
  "text/x-handlebars-template"
];
var jsxScriptTypes = [
  // https://github.com/tailwindlabs/tailwindcss-intellisense/issues/906
  "text/babel"
];
var text = { text: { match: /[^]/, lineBreaks: true } };
var states = {
  main: {
    cssBlockStart: { match: /<style(?=[>\s])/, push: "cssBlock" },
    jsBlockStart: { match: "<script", push: "jsBlock" },
    ...text
  },
  cssBlock: {
    styleStart: { match: ">", next: "style" },
    cssBlockEnd: { match: "/>", pop: 1 },
    attrStartDouble: { match: '"', push: "attrDouble" },
    attrStartSingle: { match: "'", push: "attrSingle" },
    interp: { match: "{", push: "interp" },
    ...text
  },
  jsBlock: {
    scriptStart: { match: ">", next: "script" },
    jsBlockEnd: { match: "/>", pop: 1 },
    langAttrStartDouble: { match: 'lang="', push: "langAttrDouble" },
    langAttrStartSingle: { match: "lang='", push: "langAttrSingle" },
    typeAttrStartDouble: { match: 'type="', push: "typeAttrDouble" },
    typeAttrStartSingle: { match: "type='", push: "typeAttrSingle" },
    attrStartDouble: { match: '"', push: "attrDouble" },
    attrStartSingle: { match: "'", push: "attrSingle" },
    interp: { match: "{", push: "interp" },
    ...text
  },
  interp: {
    interp: { match: "{", push: "interp" },
    end: { match: "}", pop: 1 },
    ...text
  },
  langAttrDouble: {
    langAttrEnd: { match: '"', pop: 1 },
    lang: { match: /[^"]+/, lineBreaks: true }
  },
  langAttrSingle: {
    langAttrEnd: { match: "'", pop: 1 },
    lang: { match: /[^']+/, lineBreaks: true }
  },
  typeAttrDouble: {
    langAttrEnd: { match: '"', pop: 1 },
    type: { match: /[^"]+/, lineBreaks: true }
  },
  typeAttrSingle: {
    langAttrEnd: { match: "'", pop: 1 },
    type: { match: /[^']+/, lineBreaks: true }
  },
  attrDouble: {
    attrEnd: { match: '"', pop: 1 },
    ...text
  },
  attrSingle: {
    attrEnd: { match: "'", pop: 1 },
    ...text
  },
  style: {
    cssBlockEnd: { match: /<\/style\s*>/, pop: 1 },
    ...text
  },
  script: {
    jsBlockEnd: { match: /<\/script\s*>/, pop: 1 },
    ...text
  }
};
var vueStates = {
  ...states,
  main: {
    htmlBlockStart: { match: "<template", push: "htmlBlock" },
    ...states.main
  },
  cssBlock: {
    langAttrStartDouble: { match: 'lang="', push: "langAttrDouble" },
    langAttrStartSingle: { match: "lang='", push: "langAttrSingle" },
    ...states.cssBlock
  },
  htmlBlock: {
    htmlStart: { match: ">", next: "html" },
    htmlBlockEnd: { match: "/>", pop: 1 },
    attrStartDouble: { match: '"', push: "attrDouble" },
    attrStartSingle: { match: "'", push: "attrSingle" },
    interp: { match: "{", push: "interp" },
    ...text
  },
  html: {
    htmlBlockEnd: { match: "</template>", pop: 1 },
    nestedBlockStart: { match: "<template", push: "nestedBlock" },
    ...text
  },
  nestedBlock: {
    nestedStart: { match: ">", next: "nested" },
    nestedBlockEnd: { match: "/>", pop: 1 },
    ...text
  },
  nested: {
    nestedBlockEnd: { match: "</template>", pop: 1 },
    nestedBlockStart: { match: "<template", push: "nestedBlock" },
    ...text
  }
};
var defaultLexer = moo3.states(states);
var vueLexer = moo3.states(vueStates);
var cache = new Cache({ max: 25, maxAge: 1e3 });
function getLanguageBoundaries(state, doc, text2 = doc.getText()) {
  let cacheKey = `${doc.languageId}:${text2}`;
  let cachedBoundaries = cache.get(cacheKey);
  if (cachedBoundaries !== void 0) {
    return cachedBoundaries;
  }
  let isJs = isJsDoc(state, doc);
  let defaultType = isVueDoc(doc) ? "none" : isHtmlDoc(state, doc) || isSvelteDoc(doc) ? "html" : isJs ? "jsx" : null;
  if (defaultType === null) {
    cache.set(cacheKey, null);
    return null;
  }
  text2 = getTextWithoutComments(text2, isJs ? "js" : "html");
  let lexer = defaultType === "none" ? vueLexer : defaultLexer;
  lexer.reset(text2);
  let type = defaultType;
  let boundaries = [
    { type: defaultType, range: { start: { line: 0, character: 0 }, end: void 0 } }
  ];
  let offset = 0;
  try {
    for (let token of lexer) {
      if (!token.type.startsWith("nested")) {
        if (token.type.endsWith("BlockStart")) {
          let position = indexToPosition(text2, offset);
          if (!boundaries[boundaries.length - 1].range.end) {
            boundaries[boundaries.length - 1].range.end = position;
          }
          type = token.type.replace(/BlockStart$/, "");
          boundaries.push({ type, range: { start: position, end: void 0 } });
        } else if (token.type.endsWith("BlockEnd")) {
          let position = indexToPosition(text2, offset);
          boundaries[boundaries.length - 1].range.end = position;
          boundaries.push({ type: defaultType, range: { start: position, end: void 0 } });
        } else if (token.type === "lang") {
          boundaries[boundaries.length - 1].type = token.text;
        } else if (token.type === "type" && htmlScriptTypes.includes(token.text)) {
          boundaries[boundaries.length - 1].type = "html";
        } else if (token.type === "type" && jsxScriptTypes.includes(token.text)) {
          boundaries[boundaries.length - 1].type = "jsx";
        }
      }
      offset += token.text.length;
    }
  } catch {
    cache.set(cacheKey, null);
    return null;
  }
  if (!boundaries[boundaries.length - 1].range.end) {
    boundaries[boundaries.length - 1].range.end = indexToPosition(text2, offset);
  }
  cache.set(cacheKey, boundaries);
  for (let boundary of boundaries) {
    if (boundary.type === "css")
      continue;
    if (!isCssLanguage(state, boundary.type))
      continue;
    boundary.lang = boundary.type;
    boundary.type = "css";
  }
  return boundaries;
}

// src/util/html.ts
function isHtmlDoc(state, doc) {
  const userHtmlLanguages = Object.keys(state.editor.userLanguages).filter(
    (lang) => htmlLanguages.includes(state.editor.userLanguages[lang])
  );
  return [...htmlLanguages, ...userHtmlLanguages].indexOf(doc.languageId) !== -1;
}
function isVueDoc(doc) {
  return doc.languageId === "vue";
}
function isSvelteDoc(doc) {
  return doc.languageId === "svelte";
}
function isHtmlContext(state, doc, position) {
  let str = doc.getText({
    start: { line: 0, character: 0 },
    end: position
  });
  let boundaries = getLanguageBoundaries(state, doc, str);
  return boundaries ? boundaries[boundaries.length - 1].type === "html" : false;
}

// src/util/stringify.ts
import dlv3 from "dlv";
import escapeClassName from "css.escape";
import stringifyObject from "stringify-object";
function stringifyConfigValue(x) {
  if (isObject(x))
    return `${Object.keys(x).length} values`;
  if (typeof x === "function")
    return "\u0192";
  if (typeof x === "string")
    return x;
  return stringifyObject(x, {
    inlineCharacterLimit: Infinity,
    singleQuotes: false,
    transform: (obj, prop, originalResult) => {
      if (typeof obj[prop] === "function") {
        return "\u0192";
      }
      return originalResult;
    }
  });
}
function stringifyCss(className, obj, settings) {
  if (obj.__rule !== true && !Array.isArray(obj))
    return null;
  if (Array.isArray(obj)) {
    const rules = obj.map((x) => stringifyCss(className, x, settings)).filter(Boolean);
    if (rules.length === 0)
      return null;
    return rules.join("\n\n");
  }
  let css = ``;
  const indent = " ".repeat(settings.editor.tabSize);
  const context = dlv3(obj, "__context", []);
  const props = Object.keys(removeMeta(obj));
  if (props.length === 0)
    return null;
  for (let i = 0; i < context.length; i++) {
    css += `${indent.repeat(i)}${context[i]} {
`;
  }
  const indentStr = indent.repeat(context.length);
  const decls = props.reduce((acc, curr, i) => {
    const propStr = ensureArray(obj[curr]).map((val) => `${indentStr + indent}${curr}: ${val};`).join("\n");
    return `${acc}${i === 0 ? "" : "\n"}${propStr}`;
  }, "");
  css += `${indentStr}${augmentClassName(className, obj)} {
${decls}
${indentStr}}`;
  for (let i = context.length - 1; i >= 0; i--) {
    css += `${indent.repeat(i)}
}`;
  }
  css = addEquivalents(css, settings.tailwindCSS);
  return css;
}
function augmentClassName(className, obj) {
  const pseudo = obj.__pseudo.join("");
  const scope = obj.__scope ? `${obj.__scope} ` : "";
  return `${scope}.${escapeClassName(className)}${pseudo}`;
}

// src/util/screens.ts
function isRawScreen(screen) {
  return isObject(screen) && screen.raw !== void 0;
}
function stringifyScreen(screen) {
  if (!screen)
    return void 0;
  if (typeof screen === "string")
    return `@media (min-width: ${screen})`;
  if (isRawScreen(screen)) {
    return `@media ${screen.raw}`;
  }
  let str = (Array.isArray(screen) ? screen : [screen]).map((range) => {
    return [
      typeof range.min === "string" ? `(min-width: ${range.min})` : null,
      typeof range.max === "string" ? `(max-width: ${range.max})` : null
    ].filter(Boolean).join(" and ");
  }).join(", ");
  return str ? `@media ${str}` : void 0;
}

// src/util/braceLevel.ts
function braceLevel(text2) {
  let count = 0;
  for (let i = text2.length - 1; i >= 0; i--) {
    let char = text2.charCodeAt(i);
    count += Number(
      char === 123
      /* { */
    ) - Number(
      char === 125
      /* } */
    );
  }
  return count;
}

// src/completionProvider.ts
import * as emmetHelper from "vscode-emmet-helper-bundled";

// src/util/isValidLocationForEmmetAbbreviation.ts
function isValidLocationForEmmetAbbreviation(document, abbreviationRange) {
  const startAngle = "<";
  const endAngle = ">";
  const escape = "\\";
  const question = "?";
  let start = { line: 0, character: 0 };
  let textToBackTrack = document.getText({
    start: {
      line: start.line,
      character: start.character
    },
    end: {
      line: abbreviationRange.start.line,
      character: abbreviationRange.start.character
    }
  });
  if (textToBackTrack.length > 500) {
    textToBackTrack = textToBackTrack.substr(textToBackTrack.length - 500);
  }
  if (!textToBackTrack.trim()) {
    return true;
  }
  let valid = true;
  let foundSpace = false;
  let i = textToBackTrack.length - 1;
  if (textToBackTrack[i] === startAngle) {
    return false;
  }
  while (i >= 0) {
    const char = textToBackTrack[i];
    i--;
    if (!foundSpace && /\s/.test(char)) {
      foundSpace = true;
      continue;
    }
    if (char === question && textToBackTrack[i] === startAngle) {
      i--;
      continue;
    }
    if (/\s/.test(char) && textToBackTrack[i] === startAngle) {
      i--;
      continue;
    }
    if (char !== startAngle && char !== endAngle) {
      continue;
    }
    if (i >= 0 && textToBackTrack[i] === escape) {
      i--;
      continue;
    }
    if (char === endAngle) {
      if (i >= 0 && textToBackTrack[i] === "=") {
        continue;
      } else {
        break;
      }
    }
    if (char === startAngle) {
      valid = !foundSpace;
      break;
    }
  }
  return valid;
}

// src/util/naturalExpand.ts
function naturalExpand(value, total) {
  let length = typeof total === "number" ? total.toString().length : 8;
  return ("0".repeat(length) + value).slice(-length);
}

// src/util/semver.ts
import semverGte from "semver/functions/gte";
import semverLte from "semver/functions/lte";
function gte(v1, v2) {
  if (v1.startsWith("0.0.0-insiders")) {
    return true;
  }
  return semverGte(v1, v2);
}

// src/util/docsUrl.ts
function docsUrl(version, paths) {
  let major = 0;
  let url = "https://tailwindcss-v0.netlify.app/docs/";
  if (gte(version, "0.99.0")) {
    major = 1;
    url = "https://v1.tailwindcss.com/docs/";
  }
  if (gte(version, "1.99.0")) {
    major = 2;
    url = "https://tailwindcss.com/docs/";
  }
  const path = Array.isArray(paths) ? paths[major] || paths[paths.length - 1] : paths;
  return `${url}${path}`;
}

// src/util/getClassNameMeta.ts
import dlv4 from "dlv";
function getClassNameMeta(state, classNameOrParts) {
  const parts = Array.isArray(classNameOrParts) ? classNameOrParts : getClassNameParts(state, classNameOrParts);
  if (!parts)
    return null;
  const info = dlv4(state.classNames.classNames, [...parts, "__info"]);
  if (Array.isArray(info)) {
    return info.map((i) => ({
      source: i.__source,
      pseudo: i.__pseudo,
      scope: i.__scope,
      context: i.__context
    }));
  }
  return {
    source: info.__source,
    pseudo: info.__pseudo,
    scope: info.__scope,
    context: info.__context
  };
}

// src/util/flagEnabled.ts
import dlv5 from "dlv";
function flagEnabled(state, flag) {
  if (state.featureFlags.future.includes(flag)) {
    return state.config.future === "all" || dlv5(state.config, ["future", flag], false);
  }
  if (state.featureFlags.experimental.includes(flag)) {
    return state.config.experimental === "all" || dlv5(state.config, ["experimental", flag], false);
  }
  return false;
}

// src/util/validateApply.ts
function validateApply(state, classNameOrParts) {
  if (state.jit) {
    return { isApplyable: true };
  }
  const meta = getClassNameMeta(state, classNameOrParts);
  if (!meta)
    return null;
  if (gte(state.version, "2.0.0-alpha.1") || flagEnabled(state, "applyComplexClasses")) {
    return { isApplyable: true };
  }
  const className = Array.isArray(classNameOrParts) ? classNameOrParts.join(state.separator) : classNameOrParts;
  let reason;
  if (Array.isArray(meta)) {
    reason = `'@apply' cannot be used with '${className}' because it is included in multiple rulesets.`;
  } else if (meta.source !== "utilities") {
    reason = `'@apply' cannot be used with '${className}' because it is not a utility.`;
  } else if (meta.context && meta.context.length > 0) {
    if (meta.context.length === 1) {
      reason = `'@apply' cannot be used with '${className}' because it is nested inside of an at-rule ('${meta.context[0]}').`;
    } else {
      reason = `'@apply' cannot be used with '${className}' because it is nested inside of at-rules (${meta.context.map((c) => `'${c}'`).join(", ")}).`;
    }
  } else if (meta.pseudo && meta.pseudo.length > 0) {
    if (meta.pseudo.length === 1) {
      reason = `'@apply' cannot be used with '${className}' because its definition includes a pseudo-selector ('${meta.pseudo[0]}')`;
    } else {
      reason = `'@apply' cannot be used with '${className}' because its definition includes pseudo-selectors (${meta.pseudo.map((p) => `'${p}'`).join(", ")}).`;
    }
  }
  if (reason) {
    return { isApplyable: false, reason };
  }
  return { isApplyable: true };
}

// src/util/getVariantsFromClassName.ts
function getVariantsFromClassName(state, className) {
  let allVariants = state.variants.flatMap((variant) => {
    if (variant.values.length) {
      return variant.values.map(
        (value) => value === "DEFAULT" ? variant.name : `${variant.name}${variant.hasDash ? "-" : ""}${value}`
      );
    }
    return [variant.name];
  });
  let variants = /* @__PURE__ */ new Set();
  let offset = 0;
  let parts = splitAtTopLevelOnly(className, state.separator);
  if (parts.length < 2) {
    return { variants: Array.from(variants), offset };
  }
  parts = parts.filter(Boolean);
  for (let part of parts) {
    if (allVariants.includes(part) || state.jit && (part.includes("[") && part.endsWith("]") || part.includes("/")) && generateRules(state, [`${part}${state.separator}[color:red]`]).rules.length > 0) {
      variants.add(part);
      offset += part.length + state.separator.length;
      continue;
    }
    break;
  }
  return { variants: Array.from(variants), offset };
}
function splitAtTopLevelOnly(input, separator) {
  let stack = [];
  let parts = [];
  let lastPos = 0;
  for (let idx = 0; idx < input.length; idx++) {
    let char = input[idx];
    if (stack.length === 0 && char === separator[0]) {
      if (separator.length === 1 || input.slice(idx, idx + separator.length) === separator) {
        parts.push(input.slice(lastPos, idx));
        lastPos = idx + separator.length;
      }
    }
    if (char === "(" || char === "[" || char === "{") {
      stack.push(char);
    } else if (char === ")" && stack[stack.length - 1] === "(" || char === "]" && stack[stack.length - 1] === "[" || char === "}" && stack[stack.length - 1] === "{") {
      stack.pop();
    }
  }
  parts.push(input.slice(lastPos));
  return parts;
}

// src/completionProvider.ts
import * as postcss3 from "postcss";
var isUtil = (className) => Array.isArray(className.__info) ? className.__info.some((x) => x.__source === "utilities") : className.__info.__source === "utilities";
function completionsFromClassList(state, classList, classListRange, rootFontSize, filter, context) {
  let classNames = classList.split(/[\s+]/);
  const partialClassName = classNames[classNames.length - 1];
  let sep = state.separator;
  let parts = partialClassName.split(sep);
  let subset;
  let subsetKey = [];
  let isSubset = false;
  let replacementRange = {
    ...classListRange,
    start: {
      ...classListRange.start,
      character: classListRange.end.character - partialClassName.length
    }
  };
  if (state.v4) {
    let variantItem = function(item) {
      return {
        kind: 9,
        data: {
          ...state.completionItemData ?? {},
          _type: "variant"
        },
        command: item.insertTextFormat === 2 ? void 0 : {
          title: "",
          command: "editor.action.triggerSuggest"
        },
        sortText: "-" + naturalExpand(variantOrder++),
        ...item
      };
    };
    let { variants: existingVariants, offset } = getVariantsFromClassName(state, partialClassName);
    if (context && (context.triggerKind === 1 || context.triggerKind === 2 && context.triggerCharacter === "/") && partialClassName.includes("/")) {
      let modifiers;
      let beforeSlash = partialClassName.split("/").slice(0, -1).join("/");
      let baseClassName = beforeSlash.slice(offset);
      modifiers = state.classList.find((cls) => Array.isArray(cls) && cls[0] === baseClassName)?.[1]?.modifiers;
      if (modifiers) {
        return withDefaults(
          {
            isIncomplete: false,
            items: modifiers.map((modifier, index) => {
              let className = `${beforeSlash}/${modifier}`;
              let kind = CompletionItemKind.Constant;
              let documentation;
              const color = getColor(state, className);
              if (color !== null) {
                kind = CompletionItemKind.Color;
                if (typeof color !== "string" && (color.alpha ?? 1) !== 0) {
                  documentation = formatColor(color);
                }
              }
              return {
                label: className,
                ...documentation ? { documentation } : {},
                kind,
                sortText: naturalExpand(index)
              };
            })
          },
          {
            range: replacementRange,
            data: state.completionItemData
          },
          state.editor.capabilities.itemDefaults
        );
      }
    }
    replacementRange.start.character += offset;
    let important = partialClassName.substr(offset).endsWith("!");
    if (important) {
      replacementRange.end.character -= 1;
    }
    let items = [];
    let seenVariants = /* @__PURE__ */ new Set();
    let variantOrder = 0;
    for (let variant of state.variants) {
      if (existingVariants.includes(variant.name)) {
        continue;
      }
      if (seenVariants.has(variant.name)) {
        continue;
      }
      seenVariants.add(variant.name);
      if (variant.isArbitrary) {
        items.push(
          variantItem({
            label: `${variant.name}${variant.hasDash ? "-" : ""}[]${sep}`,
            insertTextFormat: 2,
            textEditText: `${variant.name}${variant.hasDash ? "-" : ""}[\${1}]${sep}\${0}`
            // command: {
            //   title: '',
            //   command: 'tailwindCSS.onInsertArbitraryVariantSnippet',
            //   arguments: [variant.name, replacementRange],
            // },
          })
        );
      } else {
        let shouldSortVariants = !gte(state.version, "2.99.0");
        let resultingVariants = [...existingVariants, variant.name];
        if (shouldSortVariants) {
          let allVariants = state.variants.map(({ name }) => name);
          resultingVariants = resultingVariants.sort(
            (a, b) => allVariants.indexOf(b) - allVariants.indexOf(a)
          );
        }
        let selectors = [];
        try {
          selectors = variant.selectors();
        } catch (err) {
          console.log("Error while trying to get selectors for variant");
          console.log({
            variant,
            err
          });
        }
        if (selectors.length === 0) {
          continue;
        }
        items.push(
          variantItem({
            label: `${variant.name}${sep}`,
            detail: selectors.map((selector) => addPixelEquivalentsToMediaQuery(selector, rootFontSize)).join(", "),
            textEditText: resultingVariants[resultingVariants.length - 1] + sep,
            additionalTextEdits: shouldSortVariants && resultingVariants.length > 1 ? [
              {
                newText: resultingVariants.slice(0, resultingVariants.length - 1).join(sep) + sep,
                range: {
                  start: {
                    ...classListRange.start,
                    character: classListRange.end.character - partialClassName.length
                  },
                  end: {
                    ...replacementRange.start,
                    character: replacementRange.start.character
                  }
                }
              }
            ] : []
          })
        );
      }
      for (let value of variant.values ?? []) {
        if (existingVariants.includes(`${variant.name}-${value}`)) {
          continue;
        }
        if (seenVariants.has(`${variant.name}-${value}`)) {
          continue;
        }
        seenVariants.add(`${variant.name}-${value}`);
        let selectors = [];
        try {
          selectors = variant.selectors({ value });
        } catch (err) {
          console.log("Error while trying to get selectors for variant");
          console.log({
            variant,
            err
          });
        }
        if (selectors.length === 0) {
          continue;
        }
        items.push(
          variantItem({
            label: value === "DEFAULT" ? `${variant.name}${sep}` : `${variant.name}${variant.hasDash ? "-" : ""}${value}${sep}`,
            detail: selectors.join(", ")
          })
        );
      }
    }
    return withDefaults(
      {
        isIncomplete: false,
        items: items.concat(
          state.classList.reduce((items2, [className, { color }], index) => {
            if (state.blocklist?.includes([...existingVariants, className].join(state.separator))) {
              return items2;
            }
            let kind = color ? CompletionItemKind.Color : CompletionItemKind.Constant;
            let documentation;
            if (color && typeof color !== "string") {
              documentation = formatColor(color);
            }
            items2.push({
              label: className,
              kind,
              ...documentation ? { documentation } : {},
              sortText: naturalExpand(index, state.classList.length)
            });
            return items2;
          }, [])
        )
      },
      {
        data: {
          ...state.completionItemData ?? {},
          ...important ? { important } : {},
          variants: existingVariants
        },
        range: replacementRange
      },
      state.editor.capabilities.itemDefaults
    );
  }
  if (state.jit) {
    let { variants: existingVariants, offset } = getVariantsFromClassName(state, partialClassName);
    if (context && (context.triggerKind === 1 || context.triggerKind === 2 && context.triggerCharacter === "/") && partialClassName.includes("/")) {
      let modifiers;
      let beforeSlash = partialClassName.split("/").slice(0, -1).join("/");
      if (state.classListContainsMetadata) {
        let baseClassName = beforeSlash.slice(offset);
        modifiers = state.classList.find(
          (cls) => Array.isArray(cls) && cls[0] === baseClassName
        )?.[1]?.modifiers;
      } else {
        let testClass = beforeSlash + "/[0]";
        let { rules } = generateRules(state, [testClass]);
        if (rules.length > 0) {
          let opacities = dlv6(state.config, "theme.opacity", {});
          if (!isObject(opacities)) {
            opacities = {};
          }
          modifiers = Object.keys(opacities);
        }
      }
      if (modifiers) {
        return withDefaults(
          {
            isIncomplete: false,
            items: modifiers.map((modifier, index) => {
              let className = `${beforeSlash}/${modifier}`;
              let kind = CompletionItemKind.Constant;
              let documentation;
              const color = getColor(state, className);
              if (color !== null) {
                kind = CompletionItemKind.Color;
                if (typeof color !== "string" && (color.alpha ?? 1) !== 0) {
                  documentation = formatColor(color);
                }
              }
              return {
                label: className,
                ...documentation ? { documentation } : {},
                kind,
                sortText: naturalExpand(index)
              };
            })
          },
          {
            range: replacementRange,
            data: state.completionItemData
          },
          state.editor.capabilities.itemDefaults
        );
      }
    }
    replacementRange.start.character += offset;
    let important = partialClassName.substr(offset).startsWith("!");
    if (important) {
      replacementRange.start.character += 1;
    }
    let items = [];
    let seenVariants = /* @__PURE__ */ new Set();
    if (!important) {
      let variantItem = function(item) {
        return {
          kind: 9,
          data: {
            ...state.completionItemData ?? {},
            _type: "variant"
          },
          command: item.insertTextFormat === 2 ? void 0 : {
            title: "",
            command: "editor.action.triggerSuggest"
          },
          sortText: "-" + naturalExpand(variantOrder++),
          ...item
        };
      };
      let variantOrder = 0;
      for (let variant of state.variants) {
        if (existingVariants.includes(variant.name)) {
          continue;
        }
        if (seenVariants.has(variant.name)) {
          continue;
        }
        seenVariants.add(variant.name);
        if (variant.isArbitrary) {
          items.push(
            variantItem({
              label: `${variant.name}${variant.hasDash ? "-" : ""}[]${sep}`,
              insertTextFormat: 2,
              textEditText: `${variant.name}${variant.hasDash ? "-" : ""}[\${1}]${sep}\${0}`
              // command: {
              //   title: '',
              //   command: 'tailwindCSS.onInsertArbitraryVariantSnippet',
              //   arguments: [variant.name, replacementRange],
              // },
            })
          );
        } else {
          let shouldSortVariants = !gte(state.version, "2.99.0");
          let resultingVariants = [...existingVariants, variant.name];
          if (shouldSortVariants) {
            let allVariants = state.variants.map(({ name }) => name);
            resultingVariants = resultingVariants.sort(
              (a, b) => allVariants.indexOf(b) - allVariants.indexOf(a)
            );
          }
          items.push(
            variantItem({
              label: `${variant.name}${sep}`,
              detail: variant.selectors().map((selector) => addPixelEquivalentsToMediaQuery(selector, rootFontSize)).join(", "),
              textEditText: resultingVariants[resultingVariants.length - 1] + sep,
              additionalTextEdits: shouldSortVariants && resultingVariants.length > 1 ? [
                {
                  newText: resultingVariants.slice(0, resultingVariants.length - 1).join(sep) + sep,
                  range: {
                    start: {
                      ...classListRange.start,
                      character: classListRange.end.character - partialClassName.length
                    },
                    end: {
                      ...replacementRange.start,
                      character: replacementRange.start.character
                    }
                  }
                }
              ] : []
            })
          );
        }
        for (let value of variant.values ?? []) {
          if (existingVariants.includes(`${variant.name}-${value}`)) {
            continue;
          }
          if (seenVariants.has(`${variant.name}-${value}`)) {
            continue;
          }
          seenVariants.add(`${variant.name}-${value}`);
          items.push(
            variantItem({
              label: value === "DEFAULT" ? `${variant.name}${sep}` : `${variant.name}${variant.hasDash ? "-" : ""}${value}${sep}`,
              detail: variant.selectors({ value }).join(", ")
            })
          );
        }
      }
    }
    if (state.classList) {
      return withDefaults(
        {
          isIncomplete: false,
          items: items.concat(
            state.classList.reduce((items2, [className, { color }], index) => {
              if (state.blocklist?.includes([...existingVariants, className].join(state.separator))) {
                return items2;
              }
              let kind = color ? CompletionItemKind.Color : CompletionItemKind.Constant;
              let documentation;
              if (color && typeof color !== "string") {
                documentation = formatColor(color);
              }
              items2.push({
                label: className,
                kind,
                ...documentation ? { documentation } : {},
                sortText: naturalExpand(index, state.classList.length)
              });
              return items2;
            }, [])
          )
        },
        {
          data: {
            ...state.completionItemData ?? {},
            ...important ? { important } : {},
            variants: existingVariants
          },
          range: replacementRange
        },
        state.editor.capabilities.itemDefaults
      );
    }
    return withDefaults(
      {
        isIncomplete: false,
        items: items.concat(
          Object.keys(state.classNames.classNames).filter((className) => {
            let item = state.classNames.classNames[className];
            if (existingVariants.length === 0) {
              return item.__info;
            }
            return item.__info && isUtil(item);
          }).map((className, index, classNames2) => {
            let kind = CompletionItemKind.Constant;
            let documentation;
            const color = getColor(state, className);
            if (color !== null) {
              kind = CompletionItemKind.Color;
              if (typeof color !== "string" && (color.alpha ?? 1) !== 0) {
                documentation = formatColor(color);
              }
            }
            return {
              label: className,
              kind,
              ...documentation ? { documentation } : {},
              sortText: naturalExpand(index, classNames2.length)
            };
          })
        ).filter((item) => {
          if (item === null) {
            return false;
          }
          if (filter && !filter(item)) {
            return false;
          }
          return true;
        })
      },
      {
        range: replacementRange,
        data: {
          ...state.completionItemData ?? {},
          variants: existingVariants,
          ...important ? { important } : {}
        }
      },
      state.editor.capabilities.itemDefaults
    );
  }
  for (let i = parts.length - 1; i > 0; i--) {
    let keys = parts.slice(0, i).filter(Boolean);
    subset = dlv6(state.classNames.classNames, keys);
    if (typeof subset !== "undefined" && typeof dlv6(subset, ["__info", "__rule"]) === "undefined") {
      isSubset = true;
      subsetKey = keys;
      replacementRange = {
        ...replacementRange,
        start: {
          ...replacementRange.start,
          character: replacementRange.start.character + keys.join(sep).length + sep.length
        }
      };
      break;
    }
  }
  return withDefaults(
    {
      isIncomplete: false,
      items: Object.keys(isSubset ? subset : state.classNames.classNames).filter((k) => k !== "__info").filter((className) => isContextItem(state, [...subsetKey, className])).map((className, index, classNames2) => {
        return {
          label: className + sep,
          kind: 9,
          command: {
            title: "",
            command: "editor.action.triggerSuggest"
          },
          sortText: "-" + naturalExpand(index, classNames2.length),
          data: {
            ...state.completionItemData ?? {},
            className,
            variants: subsetKey
          }
        };
      }).concat(
        Object.keys(isSubset ? subset : state.classNames.classNames).filter(
          (className) => dlv6(state.classNames.classNames, [...subsetKey, className, "__info"])
        ).map((className, index, classNames2) => {
          let kind = CompletionItemKind.Constant;
          let documentation;
          const color = getColor(state, className);
          if (color !== null) {
            kind = CompletionItemKind.Color;
            if (typeof color !== "string" && (color.alpha ?? 1) !== 0) {
              documentation = formatColor(color);
            }
          }
          return {
            label: className,
            kind,
            ...documentation ? { documentation } : {},
            sortText: naturalExpand(index, classNames2.length)
          };
        })
      ).filter((item) => {
        if (item === null) {
          return false;
        }
        if (filter && !filter(item)) {
          return false;
        }
        return true;
      })
    },
    {
      range: replacementRange,
      data: {
        ...state.completionItemData ?? {},
        variants: subsetKey
      }
    },
    state.editor.capabilities.itemDefaults
  );
}
async function provideClassAttributeCompletions(state, document, position, context) {
  let str = document.getText({
    start: document.positionAt(Math.max(0, document.offsetAt(position) - 2e3)),
    end: position
  });
  let settings = (await state.editor.getConfiguration(document.uri)).tailwindCSS;
  let matches = matchClassAttributes(str, settings.classAttributes);
  if (matches.length === 0) {
    return null;
  }
  let match = matches[matches.length - 1];
  const lexer = match[0][0] === ":" || match[1].startsWith("[") && match[1].endsWith("]") ? getComputedClassAttributeLexer() : getClassAttributeLexer();
  lexer.reset(str.substr(match.index + match[0].length - 1));
  try {
    let tokens = Array.from(lexer);
    let last = tokens[tokens.length - 1];
    if (last.type.startsWith("start") || last.type === "classlist" || last.type.startsWith("arb")) {
      let classList = "";
      for (let i = tokens.length - 1; i >= 0; i--) {
        if (tokens[i].type === "classlist" || tokens[i].type.startsWith("arb")) {
          classList = tokens[i].value + classList;
        } else {
          break;
        }
      }
      return completionsFromClassList(
        state,
        classList,
        {
          start: {
            line: position.line,
            character: position.character - classList.length
          },
          end: position
        },
        settings.rootFontSize,
        void 0,
        context
      );
    }
  } catch (_) {
  }
  return null;
}
async function provideCustomClassNameCompletions(state, document, position, context) {
  const settings = await state.editor.getConfiguration(document.uri);
  const filters = settings.tailwindCSS.experimental.classRegex;
  if (filters.length === 0)
    return null;
  const cursor = document.offsetAt(position);
  let text2 = document.getText({
    start: document.positionAt(0),
    end: document.positionAt(cursor + 2e3)
  });
  for (let match of customClassesIn({ text: text2, cursor, filters })) {
    return completionsFromClassList(
      state,
      match.classList,
      {
        start: {
          line: position.line,
          character: position.character - match.classList.length
        },
        end: position
      },
      settings.tailwindCSS.rootFontSize,
      void 0,
      context
    );
  }
  return null;
}
function provideThemeVariableCompletions(state, document, position, _context) {
  if (!isCssContext(state, document, position))
    return null;
  let text2 = getTextWithoutComments(document, "css", {
    start: { line: 0, character: 0 },
    end: position
  });
  if (!text2.endsWith("-"))
    return null;
  let themeBlock = text2.lastIndexOf("@theme");
  if (themeBlock === -1)
    return null;
  if (braceLevel(text2.slice(themeBlock)) !== 1)
    return null;
  function themeVar(label) {
    return {
      label,
      kind: CompletionItemKind.Variable
      // insertTextFormat: InsertTextFormat.Snippet,
      // textEditText: `${label}-[\${1}]`,
    };
  }
  function themeNamespace(label) {
    return {
      label: `${label}-`,
      kind: CompletionItemKind.Variable
      // insertTextFormat: InsertTextFormat.Snippet,
      // textEditText: `${label}-[\${1}]`,
    };
  }
  return withDefaults(
    {
      isIncomplete: false,
      items: [
        themeVar("--default-transition-duration"),
        themeVar("--default-transition-timing-function"),
        themeVar("--default-font-family"),
        themeVar("--default-font-feature-settings"),
        themeVar("--default-font-variation-settings"),
        themeVar("--default-mono-font-family"),
        themeVar("--default-mono-font-feature-settings"),
        themeVar("--default-mono-font-variation-settings"),
        themeNamespace("--breakpoint"),
        themeNamespace("--color"),
        themeNamespace("--animate"),
        themeNamespace("--blur"),
        themeNamespace("--radius"),
        themeNamespace("--shadow"),
        themeNamespace("--inset-shadow"),
        themeNamespace("--drop-shadow"),
        themeNamespace("--spacing"),
        themeNamespace("--width"),
        themeNamespace("--font-family"),
        themeNamespace("--font-size"),
        themeNamespace("--letter-spacing"),
        themeNamespace("--line-height"),
        themeNamespace("--transition-timing-function")
      ]
    },
    {
      data: {
        ...state.completionItemData ?? {}
      }
    },
    state.editor.capabilities.itemDefaults
  );
}
async function provideAtApplyCompletions(state, document, position, context) {
  let settings = (await state.editor.getConfiguration(document.uri)).tailwindCSS;
  let str = document.getText({
    start: { line: Math.max(position.line - 30, 0), character: 0 },
    end: position
  });
  const match = findLast(/@apply\s+(?<classList>[^;}]*)$/gi, str);
  if (match === null) {
    return null;
  }
  const classList = match.groups.classList;
  return completionsFromClassList(
    state,
    classList,
    {
      start: {
        line: position.line,
        character: position.character - classList.length
      },
      end: position
    },
    settings.rootFontSize,
    (item) => {
      if (item.kind === 9) {
        return gte(state.version, "2.0.0-alpha.1") || flagEnabled(state, "applyComplexClasses");
      }
      let variants = item.data?.variants ?? [];
      let className = item.data?.className ?? item.label;
      let validated = validateApply(state, [...variants, className]);
      return validated !== null && validated.isApplyable === true;
    },
    context
  );
}
var NUMBER_REGEX = /^(\d+\.?|\d*\.\d+)$/;
function isNumber(str) {
  return NUMBER_REGEX.test(str);
}
async function provideClassNameCompletions(state, document, position, context) {
  if (isCssContext(state, document, position)) {
    return provideAtApplyCompletions(state, document, position, context);
  }
  if (isHtmlContext(state, document, position) || isJsxContext(state, document, position)) {
    return provideClassAttributeCompletions(state, document, position, context);
  }
  return null;
}
function provideCssHelperCompletions(state, document, position) {
  if (!isCssContext(state, document, position)) {
    return null;
  }
  let text2 = document.getText({
    start: { line: position.line, character: 0 },
    // read one extra character so we can see if it's a ] later
    end: { line: position.line, character: position.character + 1 }
  });
  const match = text2.substr(0, text2.length - 1).match(/[\s:;/*(){}](?<helper>config|theme)\(\s*['"]?(?<path>[^)'"]*)$/);
  if (match === null) {
    return null;
  }
  let alpha;
  let path = match.groups.path.replace(/^['"]+/g, "");
  let matches = path.match(/^([^\s]+)(?![^\[]*\])(?:\s*\/\s*([^\/\s]*))$/);
  if (matches) {
    path = matches[1];
    alpha = matches[2];
  }
  if (alpha !== void 0) {
    return null;
  }
  let base = match.groups.helper === "config" ? state.config : dlv6(state.config, "theme", {});
  let parts = path.split(/([\[\].]+)/);
  let keys = parts.filter((_, i) => i % 2 === 0);
  let separators = parts.filter((_, i) => i % 2 !== 0);
  function totalLength(arr) {
    return arr.reduce((acc, cur) => acc + cur.length, 0);
  }
  let obj;
  let offset = keys[keys.length - 1].length;
  let separator = separators.length ? separators[separators.length - 1] : null;
  if (keys.length === 1) {
    obj = base;
  } else {
    for (let i = keys.length - 1; i > 0; i--) {
      let o = dlv6(base, keys.slice(0, i));
      if (isObject(o)) {
        obj = o;
        offset = totalLength(parts.slice(i * 2));
        separator = separators[i - 1];
        break;
      }
    }
  }
  if (!obj)
    return null;
  let editRange = {
    start: {
      line: position.line,
      character: position.character - offset
    },
    end: position
  };
  return withDefaults(
    {
      isIncomplete: false,
      items: Object.keys(obj).sort((a, z) => {
        let aIsNumber = isNumber(a);
        let zIsNumber = isNumber(z);
        if (aIsNumber && !zIsNumber) {
          return -1;
        }
        if (!aIsNumber && zIsNumber) {
          return 1;
        }
        if (aIsNumber && zIsNumber) {
          return parseFloat(a) - parseFloat(z);
        }
        return 0;
      }).map((item, index, items) => {
        let color = getColorFromValue(obj[item]);
        const replaceDot = item.indexOf(".") !== -1 && separator && separator.endsWith(".");
        const insertClosingBrace = text2.charAt(text2.length - 1) !== "]" && (replaceDot || separator && separator.endsWith("["));
        const detail = stringifyConfigValue(obj[item]);
        return {
          label: item,
          sortText: naturalExpand(index, items.length),
          commitCharacters: [!item.includes(".") && ".", !item.includes("[") && "["].filter(
            Boolean
          ),
          kind: color ? 16 : isObject(obj[item]) ? 9 : 10,
          // VS Code bug causes some values to not display in some cases
          detail: detail === "0" || detail === "transparent" ? `${detail} ` : detail,
          ...color && typeof color !== "string" && (color.alpha ?? 1) !== 0 ? { documentation: formatColor(color) } : {},
          ...insertClosingBrace ? { textEditText: `${item}]` } : {},
          additionalTextEdits: replaceDot ? [
            {
              newText: "[",
              range: {
                start: {
                  ...editRange.start,
                  character: editRange.start.character - 1
                },
                end: editRange.start
              }
            }
          ] : []
        };
      })
    },
    {
      range: editRange,
      data: {
        ...state.completionItemData ?? {},
        _type: "helper"
      }
    },
    state.editor.capabilities.itemDefaults
  );
}
function provideTailwindDirectiveCompletions(state, document, position) {
  if (!isCssContext(state, document, position)) {
    return null;
  }
  let text2 = document.getText({
    start: { line: position.line, character: 0 },
    end: position
  });
  const match = text2.match(/^\s*@tailwind\s+(?<partial>[^\s]*)$/i);
  if (match === null)
    return null;
  let items = [
    gte(state.version, "1.0.0-beta.1") ? {
      label: "base",
      documentation: {
        kind: "markdown",
        value: `This injects Tailwind\u2019s base styles and any base styles registered by plugins.

[Tailwind CSS Documentation](${docsUrl(
          state.version,
          "functions-and-directives/#tailwind"
        )})`
      }
    } : {
      label: "preflight",
      documentation: {
        kind: "markdown",
        value: `This injects Tailwind\u2019s base styles, which is a combination of Normalize.css and some additional base styles.

[Tailwind CSS Documentation](${docsUrl(
          state.version,
          "functions-and-directives/#tailwind"
        )})`
      }
    },
    {
      label: "components",
      documentation: {
        kind: "markdown",
        value: `This injects Tailwind\u2019s component classes and any component classes registered by plugins.

[Tailwind CSS Documentation](${docsUrl(
          state.version,
          "functions-and-directives/#tailwind"
        )})`
      }
    },
    {
      label: "utilities",
      documentation: {
        kind: "markdown",
        value: `This injects Tailwind\u2019s utility classes and any utility classes registered by plugins.

[Tailwind CSS Documentation](${docsUrl(
          state.version,
          "functions-and-directives/#tailwind"
        )})`
      }
    },
    state.jit && gte(state.version, "2.1.99") ? {
      label: "variants",
      documentation: {
        kind: "markdown",
        value: `Use this directive to control where Tailwind injects the utility variants.

This directive is considered an advanced escape hatch and it is recommended to omit it whenever possible. If omitted, Tailwind will append these classes to the very end of your stylesheet by default.

[Tailwind CSS Documentation](${docsUrl(
          state.version,
          "just-in-time-mode#variants-are-inserted-at-tailwind-variants"
        )})`
      }
    } : {
      label: "screens",
      documentation: {
        kind: "markdown",
        value: `Use this directive to control where Tailwind injects the responsive variations of each utility.

If omitted, Tailwind will append these classes to the very end of your stylesheet by default.

[Tailwind CSS Documentation](${docsUrl(
          state.version,
          "functions-and-directives/#tailwind"
        )})`
      }
    }
  ];
  return withDefaults(
    {
      isIncomplete: false,
      items: items.map((item) => ({
        ...item,
        kind: 21
      }))
    },
    {
      data: {
        ...state.completionItemData ?? {},
        _type: "@tailwind"
      },
      range: {
        start: {
          line: position.line,
          character: position.character - match.groups.partial.length
        },
        end: position
      }
    },
    state.editor.capabilities.itemDefaults
  );
}
function provideVariantsDirectiveCompletions(state, document, position) {
  if (!isCssContext(state, document, position)) {
    return null;
  }
  if (gte(state.version, "2.99.0")) {
    return null;
  }
  let text2 = document.getText({
    start: { line: position.line, character: 0 },
    end: position
  });
  const match = text2.match(/^\s*@variants\s+(?<partial>[^}]*)$/i);
  if (match === null)
    return null;
  const parts = match.groups.partial.split(/\s*,\s*/);
  if (/\s+/.test(parts[parts.length - 1]))
    return null;
  let possibleVariants = state.variants.flatMap((variant) => {
    if (variant.values.length) {
      return variant.values.map(
        (value) => value === "DEFAULT" ? variant.name : `${variant.name}${variant.hasDash ? "-" : ""}${value}`
      );
    }
    return [variant.name];
  });
  const existingVariants = parts.slice(0, parts.length - 1);
  if (state.jit) {
    possibleVariants.unshift("responsive");
    possibleVariants = possibleVariants.filter((v) => !state.screens.includes(v));
  }
  return withDefaults(
    {
      isIncomplete: false,
      items: possibleVariants.filter((v) => existingVariants.indexOf(v) === -1).map((variant, index, variants) => ({
        // TODO: detail
        label: variant,
        kind: 21,
        sortText: naturalExpand(index, variants.length)
      }))
    },
    {
      data: {
        ...state.completionItemData ?? {},
        _type: "variant"
      },
      range: {
        start: {
          line: position.line,
          character: position.character - parts[parts.length - 1].length
        },
        end: position
      }
    },
    state.editor.capabilities.itemDefaults
  );
}
function provideLayerDirectiveCompletions(state, document, position) {
  if (!isCssContext(state, document, position)) {
    return null;
  }
  let text2 = document.getText({
    start: { line: position.line, character: 0 },
    end: position
  });
  const match = text2.match(/^\s*@layer\s+(?<partial>[^\s]*)$/i);
  if (match === null)
    return null;
  return withDefaults(
    {
      isIncomplete: false,
      items: ["base", "components", "utilities"].map((layer, index, layers) => ({
        label: layer,
        kind: 21,
        sortText: naturalExpand(index, layers.length)
      }))
    },
    {
      data: {
        ...state.completionItemData ?? {},
        _type: "layer"
      },
      range: {
        start: {
          line: position.line,
          character: position.character - match.groups.partial.length
        },
        end: position
      }
    },
    state.editor.capabilities.itemDefaults
  );
}
function withDefaults(completionList, defaults, supportedDefaults) {
  let defaultData = supportedDefaults.includes("data");
  let defaultRange = supportedDefaults.includes("editRange");
  return {
    ...completionList,
    ...defaultData || defaultRange ? {
      itemDefaults: {
        ...defaultData && defaults.data ? { data: defaults.data } : {},
        ...defaultRange && defaults.range ? { editRange: defaults.range } : {}
      }
    } : {},
    items: defaultData && defaultRange ? completionList.items : completionList.items.map(({ textEditText, ...item }) => ({
      ...item,
      ...defaultData || !defaults.data || item.data ? {} : { data: defaults.data },
      ...defaultRange || !defaults.range ? textEditText ? { textEditText } : {} : {
        textEdit: {
          newText: textEditText ?? item.label,
          range: defaults.range
        }
      }
    }))
  };
}
function provideScreenDirectiveCompletions(state, document, position) {
  if (!isCssContext(state, document, position)) {
    return null;
  }
  let text2 = document.getText({
    start: { line: position.line, character: 0 },
    end: position
  });
  const match = text2.match(/^\s*@screen\s+(?<partial>[^\s]*)$/i);
  if (match === null)
    return null;
  const screens = dlv6(state.config, ["screens"], dlv6(state.config, ["theme", "screens"], {}));
  if (!isObject(screens))
    return null;
  return withDefaults(
    {
      isIncomplete: false,
      items: Object.keys(screens).map((screen, index) => ({
        label: screen,
        kind: 21,
        sortText: naturalExpand(index)
      }))
    },
    {
      data: {
        ...state.completionItemData ?? {},
        _type: "screen"
      },
      range: {
        start: {
          line: position.line,
          character: position.character - match.groups.partial.length
        },
        end: position
      }
    },
    state.editor.capabilities.itemDefaults
  );
}
function provideCssDirectiveCompletions(state, document, position) {
  if (!isCssContext(state, document, position)) {
    return null;
  }
  let text2 = document.getText({
    start: { line: position.line, character: 0 },
    end: position
  });
  const match = text2.match(/^\s*@(?<partial>[a-z]*)$/i);
  if (match === null)
    return null;
  const items = [
    {
      label: "@tailwind",
      documentation: {
        kind: "markdown",
        value: `Use the \`@tailwind\` directive to insert Tailwind\u2019s \`base\`, \`components\`, \`utilities\` and \`${state.jit && gte(state.version, "2.1.99") ? "variants" : "screens"}\` styles into your CSS.

[Tailwind CSS Documentation](${docsUrl(
          state.version,
          "functions-and-directives/#tailwind"
        )})`
      }
    },
    {
      label: "@screen",
      documentation: {
        kind: "markdown",
        value: `The \`@screen\` directive allows you to create media queries that reference your breakpoints by name instead of duplicating their values in your own CSS.

[Tailwind CSS Documentation](${docsUrl(
          state.version,
          "functions-and-directives/#screen"
        )})`
      }
    },
    {
      label: "@apply",
      documentation: {
        kind: "markdown",
        value: `Use \`@apply\` to inline any existing utility classes into your own custom CSS.

[Tailwind CSS Documentation](${docsUrl(
          state.version,
          "functions-and-directives/#apply"
        )})`
      }
    },
    ...gte(state.version, "1.8.0") ? [
      {
        label: "@layer",
        documentation: {
          kind: "markdown",
          value: `Use the \`@layer\` directive to tell Tailwind which "bucket" a set of custom styles belong to. Valid layers are \`base\`, \`components\`, and \`utilities\`.

[Tailwind CSS Documentation](${docsUrl(
            state.version,
            "functions-and-directives/#layer"
          )})`
        }
      }
    ] : [],
    ...gte(state.version, "2.99.0") ? [] : [
      {
        label: "@variants",
        documentation: {
          kind: "markdown",
          value: `You can generate \`responsive\`, \`hover\`, \`focus\`, \`active\`, and other variants of your own utilities by wrapping their definitions in the \`@variants\` directive.

[Tailwind CSS Documentation](${docsUrl(
            state.version,
            "functions-and-directives/#variants"
          )})`
        }
      },
      {
        label: "@responsive",
        documentation: {
          kind: "markdown",
          value: `You can generate responsive variants of your own classes by wrapping their definitions in the \`@responsive\` directive.

[Tailwind CSS Documentation](${docsUrl(
            state.version,
            "functions-and-directives/#responsive"
          )})`
        }
      }
    ],
    ...gte(state.version, "3.2.0") ? [
      {
        label: "@config",
        documentation: {
          kind: "markdown",
          value: `Use the \`@config\` directive to specify which config file Tailwind should use when compiling that CSS file.

[Tailwind CSS Documentation](${docsUrl(
            state.version,
            "functions-and-directives/#config"
          )})`
        }
      }
    ] : [],
    ...gte(state.version, "4.0.0") ? [
      {
        label: "@theme",
        documentation: {
          kind: "markdown",
          value: `Use the \`@theme\` directive to specify which config file Tailwind should use when compiling that CSS file.

[Tailwind CSS Documentation](${docsUrl(
            state.version,
            "functions-and-directives/#config"
          )})`
        }
      }
    ] : []
  ];
  return withDefaults(
    {
      isIncomplete: false,
      items: items.map((item) => ({
        ...item,
        kind: 14
      }))
    },
    {
      data: {
        ...state.completionItemData ?? {},
        _type: "directive"
      },
      range: {
        start: {
          line: position.line,
          character: position.character - match.groups.partial.length - 1
        },
        end: position
      }
    },
    state.editor.capabilities.itemDefaults
  );
}
async function provideConfigDirectiveCompletions(state, document, position) {
  if (!isCssContext(state, document, position)) {
    return null;
  }
  if (!gte(state.version, "3.2.0")) {
    return null;
  }
  let text2 = document.getText({ start: { line: position.line, character: 0 }, end: position });
  let match = text2.match(/@config\s*(?<partial>'[^']*|"[^"]*)$/);
  if (!match) {
    return null;
  }
  let partial = match.groups.partial.slice(1);
  let valueBeforeLastSlash = partial.substring(0, partial.lastIndexOf("/"));
  let valueAfterLastSlash = partial.substring(partial.lastIndexOf("/") + 1);
  return withDefaults(
    {
      isIncomplete: false,
      items: (await state.editor.readDirectory(document, valueBeforeLastSlash || ".")).filter(([name, type]) => type.isDirectory || /\.c?js$/.test(name)).map(([name, type]) => ({
        label: type.isDirectory ? name + "/" : name,
        kind: type.isDirectory ? 19 : 17,
        command: type.isDirectory ? { command: "editor.action.triggerSuggest", title: "" } : void 0
      }))
    },
    {
      data: {
        ...state.completionItemData ?? {},
        _type: "filesystem"
      },
      range: {
        start: {
          line: position.line,
          character: position.character - valueAfterLastSlash.length
        },
        end: position
      }
    },
    state.editor.capabilities.itemDefaults
  );
}
async function provideEmmetCompletions(state, document, position) {
  let settings = await state.editor.getConfiguration(document.uri);
  if (settings.tailwindCSS.emmetCompletions !== true)
    return null;
  const isHtml = !isJsDoc(state, document) && isHtmlContext(state, document, position);
  const isJs = isJsDoc(state, document) || isJsxContext(state, document, position);
  const syntax = isHtml ? "html" : isJs ? "jsx" : null;
  if (syntax === null) {
    return null;
  }
  const extractAbbreviationResults = emmetHelper.extractAbbreviation(document, position, true);
  if (!extractAbbreviationResults || !emmetHelper.isAbbreviationValid(syntax, extractAbbreviationResults.abbreviation)) {
    return null;
  }
  if (!isValidLocationForEmmetAbbreviation(document, extractAbbreviationResults.abbreviationRange)) {
    return null;
  }
  if (isJs) {
    const abbreviation = extractAbbreviationResults.abbreviation;
    if (abbreviation.startsWith("this.")) {
      return null;
    }
    const symbols = await state.editor.getDocumentSymbols(document.uri);
    if (symbols && symbols.find(
      (symbol) => abbreviation === symbol.name || abbreviation.startsWith(symbol.name + ".") && !/>|\*|\+/.test(abbreviation)
    )) {
      return null;
    }
  }
  const emmetItems = emmetHelper.doComplete(document, position, syntax, {});
  if (!emmetItems || !emmetItems.items || emmetItems.items.length !== 1) {
    return null;
  }
  if (emmetItems.items[0].label === "widows: ;") {
    return null;
  }
  const parts = emmetItems.items[0].label.split(".");
  if (parts.length < 2)
    return null;
  return completionsFromClassList(
    state,
    parts[parts.length - 1],
    {
      start: {
        line: position.line,
        character: position.character - parts[parts.length - 1].length
      },
      end: position
    },
    settings.tailwindCSS.rootFontSize
  );
}
async function doComplete2(state, document, position, context) {
  if (state === null)
    return { items: [], isIncomplete: false };
  const result = await provideClassNameCompletions(state, document, position, context) || provideCssHelperCompletions(state, document, position) || provideCssDirectiveCompletions(state, document, position) || provideScreenDirectiveCompletions(state, document, position) || provideVariantsDirectiveCompletions(state, document, position) || provideTailwindDirectiveCompletions(state, document, position) || provideLayerDirectiveCompletions(state, document, position) || await provideConfigDirectiveCompletions(state, document, position) || await provideCustomClassNameCompletions(state, document, position, context) || provideThemeVariableCompletions(state, document, position, context);
  if (result)
    return result;
  return provideEmmetCompletions(state, document, position);
}
async function resolveCompletionItem(state, item) {
  if (["helper", "directive", "variant", "layer", "@tailwind", "filesystem"].includes(
    item.data?._type
  )) {
    return item;
  }
  if (item.data?._type === "screen") {
    let screens = dlv6(state.config, ["theme", "screens"], dlv6(state.config, ["screens"], {}));
    if (!isObject(screens))
      screens = {};
    item.detail = stringifyScreen(screens[item.label]);
    return item;
  }
  let className = item.data?.className ?? item.label;
  if (item.data?.important) {
    className = `!${className}`;
  }
  let variants = item.data?.variants ?? [];
  if (state.v4) {
    if (item.kind === 9)
      return item;
    if (item.detail && item.documentation)
      return item;
    let root2 = state.designSystem.compile([[...variants, className].join(state.separator)])[0];
    let rules2 = root2.nodes.filter((node) => node.type === "rule");
    if (rules2.length === 0)
      return item;
    if (!item.detail) {
      if (rules2.length === 1) {
        let decls = [];
        root2.walkDecls((node) => {
          decls.push(node);
        });
        item.detail = await stringifyDecls(
          state,
          postcss3.rule({
            nodes: decls
          })
        );
      } else {
        item.detail = `${rules2.length} rules`;
      }
    }
    if (!item.documentation) {
      item.documentation = {
        kind: "markdown",
        value: [
          "```css",
          await stringifyRoot(state, postcss3.root({ nodes: rules2 })),
          "```"
        ].join("\n")
      };
    }
    return item;
  }
  if (state.jit) {
    if (item.kind === 9)
      return item;
    if (item.detail && item.documentation)
      return item;
    let { root: root2, rules: rules2 } = generateRules(state, [[...variants, className].join(state.separator)]);
    if (rules2.length === 0)
      return item;
    if (!item.detail) {
      if (rules2.length === 1) {
        item.detail = await stringifyDecls(state, rules2[0]);
      } else {
        item.detail = `${rules2.length} rules`;
      }
    }
    if (!item.documentation) {
      item.documentation = {
        kind: "markdown",
        value: ["```css", await stringifyRoot(state, root2), "```"].join("\n")
      };
    }
    return item;
  }
  const rules = dlv6(state.classNames.classNames, [...variants, className, "__info"]);
  if (item.kind === 9) {
    item.detail = state.classNames.context[className].join(", ");
  } else {
    item.detail = await getCssDetail(state, rules);
    if (!item.documentation) {
      const settings = await state.editor.getConfiguration();
      const css = stringifyCss([...variants, className].join(":"), rules, settings);
      if (css) {
        item.documentation = {
          kind: "markdown",
          value: ["```css", css, "```"].join("\n")
        };
      }
    }
  }
  return item;
}
function isContextItem(state, keys) {
  const item = dlv6(state.classNames.classNames, keys);
  if (!isObject(item)) {
    return false;
  }
  if (!state.classNames.context[keys[keys.length - 1]]) {
    return false;
  }
  if (Object.keys(item).filter((x) => x !== "__info").length > 0) {
    return true;
  }
  return isObject(item.__info) && !item.__info.__rule;
}
function stringifyDecls2(obj, settings) {
  let props = Object.keys(obj);
  let nonCustomProps = props.filter((prop) => !prop.startsWith("--"));
  if (props.length !== nonCustomProps.length && nonCustomProps.length !== 0) {
    props = nonCustomProps;
  }
  return props.map(
    (prop) => ensureArray(obj[prop]).map((value) => {
      if (settings.tailwindCSS.showPixelEquivalents) {
        value = addPixelEquivalentsToValue(value, settings.tailwindCSS.rootFontSize);
      }
      return `${prop}: ${value};`;
    }).join(" ")
  ).join(" ");
}
async function getCssDetail(state, className) {
  if (Array.isArray(className)) {
    return `${className.length} rules`;
  }
  if (className.__rule === true) {
    const settings = await state.editor.getConfiguration();
    return stringifyDecls2(removeMeta(className), settings);
  }
  return null;
}

// src/diagnostics/types.ts
var DiagnosticKind = /* @__PURE__ */ ((DiagnosticKind2) => {
  DiagnosticKind2["CssConflict"] = "cssConflict";
  DiagnosticKind2["InvalidApply"] = "invalidApply";
  DiagnosticKind2["InvalidScreen"] = "invalidScreen";
  DiagnosticKind2["InvalidVariant"] = "invalidVariant";
  DiagnosticKind2["InvalidConfigPath"] = "invalidConfigPath";
  DiagnosticKind2["InvalidTailwindDirective"] = "invalidTailwindDirective";
  DiagnosticKind2["RecommendedVariantOrder"] = "recommendedVariantOrder";
  return DiagnosticKind2;
})(DiagnosticKind || {});
function isCssConflictDiagnostic(diagnostic) {
  return diagnostic.code === "cssConflict" /* CssConflict */;
}
function isInvalidApplyDiagnostic(diagnostic) {
  return diagnostic.code === "invalidApply" /* InvalidApply */;
}
function isInvalidScreenDiagnostic(diagnostic) {
  return diagnostic.code === "invalidScreen" /* InvalidScreen */;
}
function isInvalidVariantDiagnostic(diagnostic) {
  return diagnostic.code === "invalidVariant" /* InvalidVariant */;
}
function isInvalidConfigPathDiagnostic(diagnostic) {
  return diagnostic.code === "invalidConfigPath" /* InvalidConfigPath */;
}
function isInvalidTailwindDirectiveDiagnostic(diagnostic) {
  return diagnostic.code === "invalidTailwindDirective" /* InvalidTailwindDirective */;
}
function isRecommendedVariantOrderDiagnostic(diagnostic) {
  return diagnostic.code === "recommendedVariantOrder" /* RecommendedVariantOrder */;
}

// src/util/joinWithAnd.ts
function joinWithAnd(strings) {
  return strings.reduce((acc, cur, i) => {
    if (i === 0) {
      return cur;
    }
    if (strings.length > 1 && i === strings.length - 1) {
      return `${acc} and ${cur}`;
    }
    return `${acc}, ${cur}`;
  }, "");
}

// src/util/getClassNameDecls.ts
import dlv7 from "dlv";
function getClassNameDecls(state, className) {
  const parts = getClassNameParts(state, className);
  if (!parts)
    return null;
  const info = dlv7(state.classNames.classNames, [...parts, "__info"]);
  if (Array.isArray(info)) {
    return info.map(removeMeta);
  }
  return removeMeta(info);
}

// src/diagnostics/getCssConflictDiagnostics.ts
function isAtRule2(node) {
  return node.type === "atrule";
}
function isKeyframes(rule2) {
  let parent = rule2.parent;
  if (!parent) {
    return false;
  }
  if (isAtRule2(parent) && parent.name === "keyframes") {
    return true;
  }
  return false;
}
function getRuleProperties(rule2) {
  let properties = [];
  rule2.walkDecls(({ prop }) => {
    properties.push(prop);
  });
  return properties;
}
async function getCssConflictDiagnostics(state, document, settings) {
  let severity = settings.tailwindCSS.lint.cssConflict;
  if (severity === "ignore")
    return [];
  let diagnostics = [];
  const classLists = await findClassListsInDocument(state, document);
  classLists.forEach((classList) => {
    const classNames = getClassNamesInClassList(classList, state.blocklist);
    if (state.v4) {
      const groups = recordClassDetails(state, classNames);
      for (let [className, conflictingClassNames] of findConflicts(classNames, groups)) {
        diagnostics.push({
          code: "cssConflict" /* CssConflict */,
          className,
          otherClassNames: conflictingClassNames,
          range: className.range,
          severity: severity === "error" ? 1 : 2,
          message: `'${className.className}' applies the same CSS properties as ${joinWithAnd(
            conflictingClassNames.map(
              (conflictingClassName) => `'${conflictingClassName.className}'`
            )
          )}.`,
          relatedInformation: conflictingClassNames.map((conflictingClassName) => {
            return {
              message: conflictingClassName.className,
              location: {
                uri: document.uri,
                range: conflictingClassName.range
              }
            };
          })
        });
      }
      return;
    }
    classNames.forEach((className, index) => {
      if (state.jit) {
        let { rules } = generateRules(
          state,
          [className.className],
          (rule2) => !isKeyframes(rule2)
        );
        if (rules.length === 0) {
          return;
        }
        let info = rules.map((rule2) => {
          let properties2 = getRuleProperties(rule2);
          let context = getRuleContext(state, rule2, className.className);
          return { context, properties: properties2 };
        });
        let otherClassNames2 = classNames.filter((_className, i) => i !== index);
        let conflictingClassNames2 = otherClassNames2.filter((otherClassName) => {
          let { rules: otherRules } = generateRules(
            state,
            [otherClassName.className],
            (rule2) => !isKeyframes(rule2)
          );
          if (otherRules.length !== rules.length) {
            return false;
          }
          let propertiesAreComparable = false;
          for (let i = 0; i < otherRules.length; i++) {
            let otherRule = otherRules[i];
            let properties2 = getRuleProperties(otherRule);
            if (info[i].properties.length > 0 && properties2.length > 0) {
              propertiesAreComparable = true;
            }
            if (!equal(info[i].properties, properties2)) {
              return false;
            }
            let context = getRuleContext(state, otherRule, otherClassName.className);
            if (!equal(info[i].context, context)) {
              return false;
            }
          }
          if (!propertiesAreComparable) {
            return false;
          }
          return true;
        });
        if (conflictingClassNames2.length === 0)
          return;
        diagnostics.push({
          code: "cssConflict" /* CssConflict */,
          className,
          otherClassNames: conflictingClassNames2,
          range: className.range,
          severity: severity === "error" ? 1 : 2,
          message: `'${className.className}' applies the same CSS properties as ${joinWithAnd(
            conflictingClassNames2.map(
              (conflictingClassName) => `'${conflictingClassName.className}'`
            )
          )}.`,
          relatedInformation: conflictingClassNames2.map((conflictingClassName) => {
            return {
              message: conflictingClassName.className,
              location: {
                uri: document.uri,
                range: conflictingClassName.range
              }
            };
          })
        });
        return;
      }
      let decls = getClassNameDecls(state, className.className);
      if (!decls)
        return;
      let properties = Object.keys(decls);
      let meta = getClassNameMeta(state, className.className);
      let otherClassNames = classNames.filter((_className, i) => i !== index);
      let conflictingClassNames = otherClassNames.filter((otherClassName) => {
        let otherDecls = getClassNameDecls(state, otherClassName.className);
        if (!otherDecls)
          return false;
        let otherMeta = getClassNameMeta(state, otherClassName.className);
        return equal(properties, Object.keys(otherDecls)) && !Array.isArray(meta) && !Array.isArray(otherMeta) && equal(meta.context, otherMeta.context) && equal(meta.pseudo, otherMeta.pseudo) && meta.scope === otherMeta.scope;
      });
      if (conflictingClassNames.length === 0)
        return;
      diagnostics.push({
        code: "cssConflict" /* CssConflict */,
        className,
        otherClassNames: conflictingClassNames,
        range: className.range,
        severity: severity === "error" ? 1 : 2,
        message: `'${className.className}' applies the same CSS ${properties.length === 1 ? "property" : "properties"} as ${joinWithAnd(
          conflictingClassNames.map(
            (conflictingClassName) => `'${conflictingClassName.className}'`
          )
        )}.`,
        relatedInformation: conflictingClassNames.map((conflictingClassName) => {
          return {
            message: conflictingClassName.className,
            location: {
              uri: document.uri,
              range: conflictingClassName.range
            }
          };
        })
      });
    });
  });
  return diagnostics;
}
function visit(nodes, cb, path = []) {
  for (let child of nodes) {
    path = [...path, child];
    cb(child, path);
    if ("nodes" in child && child.nodes && child.nodes.length > 0) {
      visit(child.nodes, cb, path);
    }
  }
}
function recordClassDetails(state, classes) {
  const groups = {};
  let roots = state.designSystem.compile(classes.map((c) => c.className));
  for (let [idx, root2] of roots.entries()) {
    let { className } = classes[idx];
    visit([root2], (node, path) => {
      if (node.type !== "rule" && node.type !== "atrule")
        return;
      let properties = [];
      for (let child of node.nodes ?? []) {
        if (child.type !== "decl")
          continue;
        properties.push(child.prop);
      }
      if (properties.length === 0)
        return;
      groups[className] ??= [];
      groups[className].push({
        properties,
        context: path.map((node2) => {
          if (node2.type === "rule") {
            return node2.selector;
          } else if (node2.type === "atrule") {
            return `@${node2.name} ${node2.params}`;
          }
          return "";
        }).filter(Boolean).slice(1)
      });
    });
  }
  return groups;
}
function* findConflicts(classes, groups) {
  for (let className of classes) {
    let entries = groups[className.className] ?? [];
    let conflictingClassNames = [];
    for (let otherClassName of classes) {
      if (className === otherClassName)
        continue;
      let otherEntries = groups[otherClassName.className] ?? [];
      if (entries.length !== otherEntries.length)
        continue;
      let hasConflict = false;
      for (let i = 0; i < entries.length; i++) {
        let entry = entries[i];
        let otherEntry = otherEntries[i];
        if (entry.properties.length !== otherEntry.properties.length) {
          hasConflict = false;
          break;
        }
        if (entry.context.length !== otherEntry.context.length) {
          hasConflict = false;
          break;
        }
        if (!equal(entry.properties, otherEntry.properties)) {
          hasConflict = false;
          break;
        }
        if (!equal(entry.context, otherEntry.context)) {
          hasConflict = false;
          break;
        }
        if (entry.properties.length === 0 && otherEntry.properties.length === 0) {
          continue;
        }
        hasConflict = true;
      }
      if (!hasConflict)
        continue;
      conflictingClassNames.push(otherClassName);
    }
    if (conflictingClassNames.length === 0)
      return;
    yield [className, conflictingClassNames];
  }
}

// src/diagnostics/getInvalidApplyDiagnostics.ts
async function getInvalidApplyDiagnostics(state, document, settings) {
  let severity = settings.tailwindCSS.lint.invalidApply;
  if (severity === "ignore")
    return [];
  const classNames = await findClassNamesInRange(state, document, void 0, "css", false);
  let diagnostics = classNames.map((className) => {
    let result = validateApply(state, className.className);
    if (result === null || result.isApplyable === true) {
      return null;
    }
    return {
      code: "invalidApply" /* InvalidApply */,
      severity: severity === "error" ? 1 : 2,
      range: className.range,
      message: result.reason,
      className
    };
  });
  return diagnostics.filter(Boolean);
}

// src/util/closest.ts
import sift from "sift-string";
function closest(input, options) {
  return options.concat([]).sort((a, b) => sift(input, a) - sift(input, b))[0];
}

// src/util/absoluteRange.ts
function absoluteRange(range, reference) {
  return {
    start: {
      line: (reference?.start.line || 0) + range.start.line,
      character: (range.end.line === 0 ? reference?.start.character || 0 : 0) + range.start.character
    },
    end: {
      line: (reference?.start.line || 0) + range.end.line,
      character: (range.end.line === 0 ? reference?.start.character || 0 : 0) + range.end.character
    }
  };
}

// src/diagnostics/getInvalidScreenDiagnostics.ts
function getInvalidScreenDiagnostics(state, document, settings) {
  let severity = settings.tailwindCSS.lint.invalidScreen;
  if (severity === "ignore")
    return [];
  let diagnostics = [];
  let ranges = [];
  if (isCssDoc(state, document)) {
    ranges.push(void 0);
  } else {
    let boundaries = getLanguageBoundaries(state, document);
    if (!boundaries)
      return [];
    ranges.push(...boundaries.filter((b) => b.type === "css").map(({ range }) => range));
  }
  ranges.forEach((range) => {
    let text2 = getTextWithoutComments(document, "css", range);
    let matches = findAll(/(?:\s|^)@screen\s+(?<screen>[^\s{]+)/g, text2);
    matches.forEach((match) => {
      if (state.screens.includes(match.groups.screen)) {
        return null;
      }
      let message = `The screen '${match.groups.screen}' does not exist in your theme config.`;
      let suggestions = [];
      let suggestion = closest(match.groups.screen, state.screens);
      if (suggestion) {
        suggestions.push(suggestion);
        message += ` Did you mean '${suggestion}'?`;
      }
      diagnostics.push({
        code: "invalidScreen" /* InvalidScreen */,
        range: absoluteRange(
          {
            start: indexToPosition(
              text2,
              match.index + match[0].length - match.groups.screen.length
            ),
            end: indexToPosition(text2, match.index + match[0].length)
          },
          range
        ),
        severity: severity === "error" ? 1 : 2,
        message,
        suggestions
      });
    });
  });
  return diagnostics;
}

// src/diagnostics/getInvalidVariantDiagnostics.ts
function getInvalidVariantDiagnostics(state, document, settings) {
  let severity = settings.tailwindCSS.lint.invalidVariant;
  if (severity === "ignore")
    return [];
  if (gte(state.version, "2.99.0")) {
    return [];
  }
  let diagnostics = [];
  let ranges = [];
  if (isCssDoc(state, document)) {
    ranges.push(void 0);
  } else {
    let boundaries = getLanguageBoundaries(state, document);
    if (!boundaries)
      return [];
    ranges.push(...boundaries.filter((b) => b.type === "css").map(({ range }) => range));
  }
  let possibleVariants = state.variants.flatMap((variant) => {
    if (variant.values.length) {
      return variant.values.map(
        (value) => value === "DEFAULT" ? variant.name : `${variant.name}${variant.hasDash ? "-" : ""}${value}`
      );
    }
    return [variant.name];
  });
  if (state.jit) {
    possibleVariants.unshift("responsive");
    possibleVariants = possibleVariants.filter((v) => !state.screens.includes(v));
  }
  ranges.forEach((range) => {
    let text2 = getTextWithoutComments(document, "css", range);
    let matches = findAll(/(?:\s|^)@variants\s+(?<variants>[^{]+)/g, text2);
    matches.forEach((match) => {
      let variants = match.groups.variants.split(/(\s*,\s*)/);
      let listStartIndex = match.index + match[0].length - match.groups.variants.length;
      for (let i = 0; i < variants.length; i += 2) {
        let variant = variants[i].trim();
        if (possibleVariants.includes(variant)) {
          continue;
        }
        let message = `The variant '${variant}' does not exist.`;
        let suggestions = [];
        let suggestion = closest(variant, possibleVariants);
        if (suggestion) {
          suggestions.push(suggestion);
          message += ` Did you mean '${suggestion}'?`;
        }
        let variantStartIndex = listStartIndex + variants.slice(0, i).join("").length;
        diagnostics.push({
          code: "invalidVariant" /* InvalidVariant */,
          range: absoluteRange(
            {
              start: indexToPosition(text2, variantStartIndex),
              end: indexToPosition(text2, variantStartIndex + variant.length)
            },
            range
          ),
          severity: severity === "error" ? 1 : 2,
          message,
          suggestions
        });
      }
    });
  });
  return diagnostics;
}

// src/util/stringToPath.ts
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;
var reEscapeChar = /\\(\\)?/g;
function stringToPath(string) {
  let result = [];
  if (string.charCodeAt(0) === 46) {
    result.push("");
  }
  string.replace(rePropName, (match, number, quote, subString) => {
    result.push(quote ? subString.replace(reEscapeChar, "$1") : number || match);
  });
  return result;
}

// src/diagnostics/getInvalidConfigPathDiagnostics.ts
import dlv8 from "dlv";
function pathToString(path) {
  if (typeof path === "string")
    return path;
  return path.reduce((acc, cur, i) => {
    if (i === 0)
      return cur;
    if (cur.includes("."))
      return `${acc}[${cur}]`;
    return `${acc}.${cur}`;
  }, "");
}
function validateConfigPath(state, path, base = []) {
  let keys = Array.isArray(path) ? path : stringToPath(path);
  let fullPath = [...base, ...keys];
  let value = dlv8(state.config, fullPath);
  let suggestions = [];
  let transformThemeValue = state.modules?.transformThemeValue?.module ?? ((_) => (value2) => value2);
  if (fullPath[0] === "theme" && fullPath[1]) {
    value = transformThemeValue(fullPath[1])(value);
  }
  function findAlternativePath() {
    let points = combinations("123456789".substr(0, keys.length - 1)).map(
      (x) => x.split("").map((x2) => parseInt(x2, 10))
    );
    let possibilities = points.map((p) => {
      let result = [];
      let i = 0;
      p.forEach((x) => {
        result.push(keys.slice(i, x).join("."));
        i = x;
      });
      result.push(keys.slice(i).join("."));
      return result;
    }).slice(1);
    return possibilities.find((possibility) => validateConfigPath(state, possibility, base).isValid);
  }
  if (typeof value === "undefined") {
    let reason = `'${pathToString(path)}' does not exist in your theme config.`;
    let parentPath = [...base, ...keys.slice(0, keys.length - 1)];
    let parentValue = dlv8(state.config, parentPath);
    if (isObject(parentValue)) {
      let closestValidKey = closest(
        keys[keys.length - 1],
        Object.keys(parentValue).filter(
          (key) => validateConfigPath(state, [...parentPath, key]).isValid
        )
      );
      if (closestValidKey) {
        suggestions.push(pathToString([...keys.slice(0, keys.length - 1), closestValidKey]));
        reason += ` Did you mean '${suggestions[0]}'?`;
      }
    } else {
      let altPath = findAlternativePath();
      if (altPath) {
        return {
          isValid: false,
          reason: `${reason} Did you mean '${pathToString(altPath)}'?`,
          suggestions: [pathToString(altPath)]
        };
      }
    }
    return {
      isValid: false,
      reason,
      suggestions
    };
  }
  if (!(typeof value === "string" || typeof value === "number" || value instanceof String || value instanceof Number || Array.isArray(value) || typeof value === "function")) {
    let reason = `'${pathToString(path)}' was found but does not resolve to a valid theme value.`;
    if (isObject(value)) {
      let validKeys = Object.keys(value).filter(
        (key) => validateConfigPath(state, [...keys, key], base).isValid
      );
      if (validKeys.length) {
        suggestions.push(...validKeys.map((validKey) => pathToString([...keys, validKey])));
        reason += ` Did you mean something like '${suggestions[0]}'?`;
      }
    }
    return {
      isValid: false,
      reason,
      suggestions
    };
  }
  let isValid = true;
  for (let i = keys.length - 1; i >= 0; i--) {
    let key = keys[i];
    let parentValue = dlv8(state.config, [...base, ...keys.slice(0, i)]);
    if (/^[0-9]+$/.test(key)) {
      if (!isObject(parentValue) && !Array.isArray(parentValue)) {
        isValid = false;
        break;
      }
    } else if (!isObject(parentValue)) {
      isValid = false;
      break;
    }
  }
  if (!isValid) {
    let reason = `'${pathToString(path)}' does not exist in your theme config.`;
    let altPath = findAlternativePath();
    if (altPath) {
      return {
        isValid: false,
        reason: `${reason} Did you mean '${pathToString(altPath)}'?`,
        suggestions: [pathToString(altPath)]
      };
    }
    return {
      isValid: false,
      reason,
      suggestions: []
    };
  }
  return {
    isValid: true,
    value
  };
}
function getInvalidConfigPathDiagnostics(state, document, settings) {
  let severity = settings.tailwindCSS.lint.invalidConfigPath;
  if (severity === "ignore")
    return [];
  let diagnostics = [];
  findHelperFunctionsInDocument(state, document).forEach((helperFn) => {
    let base = helperFn.helper === "theme" ? ["theme"] : [];
    let result = validateConfigPath(state, helperFn.path, base);
    if (result.isValid === true) {
      return;
    }
    diagnostics.push({
      code: "invalidConfigPath" /* InvalidConfigPath */,
      range: helperFn.ranges.path,
      severity: severity === "error" ? 1 : 2,
      message: result.reason,
      suggestions: result.suggestions
    });
  });
  return diagnostics;
}

// src/diagnostics/getInvalidTailwindDirectiveDiagnostics.ts
function getInvalidTailwindDirectiveDiagnostics(state, document, settings) {
  let severity = settings.tailwindCSS.lint.invalidTailwindDirective;
  if (severity === "ignore")
    return [];
  let diagnostics = [];
  let ranges = [];
  if (isCssDoc(state, document)) {
    ranges.push(void 0);
  } else {
    let boundaries = getLanguageBoundaries(state, document);
    if (!boundaries)
      return [];
    ranges.push(...boundaries.filter((b) => b.type === "css").map(({ range }) => range));
  }
  let regex;
  if (isSemicolonlessCssLanguage(document.languageId, state.editor?.userLanguages)) {
    regex = /(?:\s|^)@tailwind\s+(?<value>[^\r\n]+)/g;
  } else {
    regex = /(?:\s|^)@tailwind\s+(?<value>[^;]+)/g;
  }
  let hasVariantsDirective = state.jit && gte(state.version, "2.1.99");
  ranges.forEach((range) => {
    let text2 = getTextWithoutComments(document, "css", range);
    let matches = findAll(regex, text2);
    let valid = [
      "utilities",
      "components",
      "screens",
      gte(state.version, "1.0.0-beta.1") ? "base" : "preflight",
      hasVariantsDirective && "variants"
    ].filter(Boolean);
    let suggestable = valid;
    if (hasVariantsDirective) {
      suggestable = suggestable.filter((value) => value !== "screens");
    }
    matches.forEach((match) => {
      if (valid.includes(match.groups.value)) {
        return null;
      }
      let message = `'${match.groups.value}' is not a valid value.`;
      let suggestions = [];
      if (match.groups.value === "preflight") {
        suggestions.push("base");
        message += ` Did you mean 'base'?`;
      } else {
        let suggestion = closest(match.groups.value, suggestable);
        if (suggestion) {
          suggestions.push(suggestion);
          message += ` Did you mean '${suggestion}'?`;
        }
      }
      diagnostics.push({
        code: "invalidTailwindDirective" /* InvalidTailwindDirective */,
        range: absoluteRange(
          {
            start: indexToPosition(text2, match.index + match[0].length - match.groups.value.length),
            end: indexToPosition(text2, match.index + match[0].length)
          },
          range
        ),
        severity: severity === "error" ? 1 : 2,
        message,
        suggestions
      });
    });
  });
  return diagnostics;
}

// src/diagnostics/getRecommendedVariantOrderDiagnostics.ts
async function getRecommendedVariantOrderDiagnostics(state, document, settings) {
  if (state.v4)
    return [];
  if (!state.jit)
    return [];
  if (gte(state.version, "2.99.0"))
    return [];
  let severity = settings.tailwindCSS.lint.recommendedVariantOrder;
  if (severity === "ignore")
    return [];
  let diagnostics = [];
  const classLists = await findClassListsInDocument(state, document);
  classLists.forEach((classList) => {
    const classNames = getClassNamesInClassList(classList, state.blocklist);
    classNames.forEach((className) => {
      let { rules } = generateRules(state, [className.className]);
      if (rules.length === 0) {
        return;
      }
      let order = state.jitContext.variantOrder ?? state.jitContext.offsets.variantOffsets;
      let { variants, offset } = getVariantsFromClassName(state, className.className);
      let sortedVariants = [...variants].sort((a, b) => bigSign(order.get(b) - order.get(a)));
      if (!equalExact(variants, sortedVariants)) {
        diagnostics.push({
          code: "recommendedVariantOrder" /* RecommendedVariantOrder */,
          suggestions: [
            [...sortedVariants, className.className.substr(offset)].join(state.separator)
          ],
          range: className.range,
          severity: severity === "error" ? 1 : 2,
          message: "Variants are not in the recommended order, which may cause unexpected CSS output."
        });
      }
    });
  });
  return diagnostics;
}

// src/diagnostics/diagnosticsProvider.ts
async function doValidate(state, document, only = [
  "cssConflict" /* CssConflict */,
  "invalidApply" /* InvalidApply */,
  "invalidScreen" /* InvalidScreen */,
  "invalidVariant" /* InvalidVariant */,
  "invalidConfigPath" /* InvalidConfigPath */,
  "invalidTailwindDirective" /* InvalidTailwindDirective */,
  "recommendedVariantOrder" /* RecommendedVariantOrder */
]) {
  const settings = await state.editor.getConfiguration(document.uri);
  return settings.tailwindCSS.validate ? [
    ...only.includes("cssConflict" /* CssConflict */) ? await getCssConflictDiagnostics(state, document, settings) : [],
    ...only.includes("invalidApply" /* InvalidApply */) ? await getInvalidApplyDiagnostics(state, document, settings) : [],
    ...only.includes("invalidScreen" /* InvalidScreen */) ? getInvalidScreenDiagnostics(state, document, settings) : [],
    ...only.includes("invalidVariant" /* InvalidVariant */) ? getInvalidVariantDiagnostics(state, document, settings) : [],
    ...only.includes("invalidConfigPath" /* InvalidConfigPath */) ? getInvalidConfigPathDiagnostics(state, document, settings) : [],
    ...only.includes("invalidTailwindDirective" /* InvalidTailwindDirective */) ? getInvalidTailwindDirectiveDiagnostics(state, document, settings) : [],
    ...only.includes("recommendedVariantOrder" /* RecommendedVariantOrder */) ? await getRecommendedVariantOrderDiagnostics(state, document, settings) : []
  ] : [];
}

// src/hoverProvider.ts
import dlv9 from "dlv";
async function doHover(state, document, position) {
  return await provideClassNameHover(state, document, position) || await provideCssHelperHover(state, document, position);
}
async function provideCssHelperHover(state, document, position) {
  if (!isCssContext(state, document, position)) {
    return null;
  }
  const settings = await state.editor.getConfiguration(document.uri);
  let helperFns = findHelperFunctionsInRange(document, {
    start: { line: position.line, character: 0 },
    end: { line: position.line + 1, character: 0 }
  });
  for (let helperFn of helperFns) {
    if (!isWithinRange(position, helperFn.ranges.path))
      continue;
    let validated = validateConfigPath(
      state,
      helperFn.path,
      helperFn.helper === "theme" ? ["theme"] : []
    );
    let value = validated.isValid ? stringifyConfigValue(validated.value) : null;
    if (value === null)
      return null;
    if (settings.tailwindCSS.showPixelEquivalents) {
      value = addPixelEquivalentsToValue(value, settings.tailwindCSS.rootFontSize);
    }
    return {
      contents: { kind: "markdown", value: ["```plaintext", value, "```"].join("\n") },
      range: helperFn.ranges.path
    };
  }
  return null;
}
async function provideClassNameHover(state, document, position) {
  let className = await findClassNameAtPosition(state, document, position);
  if (className === null)
    return null;
  if (state.v4) {
    let root2 = state.designSystem.compile([className.className])[0];
    if (root2.nodes.length === 0) {
      return null;
    }
    return {
      contents: {
        language: "css",
        value: await stringifyRoot(state, root2, document.uri)
      },
      range: className.range
    };
  }
  if (state.jit) {
    let { root: root2, rules } = generateRules(state, [className.className]);
    if (rules.length === 0) {
      return null;
    }
    return {
      contents: {
        language: "css",
        value: await stringifyRoot(state, root2, document.uri)
      },
      range: className.range
    };
  }
  const parts = getClassNameParts(state, className.className);
  if (!parts)
    return null;
  if (isCssContext(state, document, position)) {
    let validated = validateApply(state, parts);
    if (validated === null || validated.isApplyable === false) {
      return null;
    }
  }
  const settings = await state.editor.getConfiguration(document.uri);
  const css = stringifyCss(
    className.className,
    dlv9(state.classNames.classNames, [...parts, "__info"]),
    settings
  );
  if (!css)
    return null;
  return {
    contents: {
      language: "css",
      value: css
    },
    range: className.range
  };
}

// src/util/removeRangesFromString.ts
import lineColumn2 from "line-column";
function removeRangesFromString(str, rangeOrRanges) {
  let ranges = ensureArray(rangeOrRanges);
  let finder = lineColumn2(str + "\n", { origin: 0 });
  let indexRanges = [];
  ranges.forEach((range) => {
    let start = finder.toIndex(range.start.line, range.start.character);
    let end = finder.toIndex(range.end.line, range.end.character);
    for (let i2 = start - 1; i2 >= 0; i2--) {
      if (/\s/.test(str.charAt(i2))) {
        start = i2;
      } else {
        break;
      }
    }
    indexRanges.push({ start, end });
  });
  indexRanges.sort((a, b) => a.start - b.start);
  let result = "";
  let i = 0;
  indexRanges.forEach((indexRange) => {
    result += str.substring(i, indexRange.start);
    i = indexRange.end;
  });
  result += str.substring(i);
  return result.trim();
}

// src/codeActions/provideCssConflictCodeActions.ts
async function provideCssConflictCodeActions(_state, params, diagnostic) {
  return [
    {
      title: `Delete ${joinWithAnd(
        diagnostic.otherClassNames.map((otherClassName) => `'${otherClassName.className}'`)
      )}`,
      kind: "quickfix",
      // CodeActionKind.QuickFix,
      diagnostics: [diagnostic],
      edit: {
        changes: {
          [params.textDocument.uri]: [
            {
              range: diagnostic.className.classList.range,
              newText: removeRangesFromString(
                diagnostic.className.classList.classList,
                diagnostic.otherClassNames.map((otherClassName) => otherClassName.relativeRange)
              )
            }
          ]
        }
      }
    }
  ];
}

// src/codeActions/provideInvalidApplyCodeActions.ts
import dlv10 from "dlv";
import detectIndent from "detect-indent";

// src/util/cssObjToAst.ts
var IMPORTANT = /\s*!important\s*$/i;
var unitless = {
  "box-flex": true,
  "box-flex-group": true,
  "column-count": true,
  flex: true,
  "flex-grow": true,
  "flex-positive": true,
  "flex-shrink": true,
  "flex-negative": true,
  "font-weight": true,
  "line-clamp": true,
  "line-height": true,
  opacity: true,
  order: true,
  orphans: true,
  "tab-size": true,
  widows: true,
  "z-index": true,
  zoom: true,
  "fill-opacity": true,
  "stroke-dashoffset": true,
  "stroke-opacity": true,
  "stroke-width": true
};
function dashify(str) {
  return str.replace(/([A-Z])/g, "-$1").replace(/^ms-/, "-ms-").toLowerCase();
}
function decl(parent, name, value, postcss4) {
  if (value === false || value === null)
    return;
  name = dashify(name);
  if (typeof value === "number") {
    if (value === 0 || unitless[name]) {
      value = value.toString();
    } else {
      value = value.toString() + "px";
    }
  }
  if (name === "css-float")
    name = "float";
  if (IMPORTANT.test(value)) {
    value = value.replace(IMPORTANT, "");
    parent.push(postcss4.decl({ prop: name, value, important: true }));
  } else {
    parent.push(postcss4.decl({ prop: name, value }));
  }
}
function atRule(parent, parts, value, postcss4) {
  var node = postcss4.atRule({ name: parts[1], params: parts[3] || "" });
  if (typeof value === "object") {
    node.nodes = [];
    parse2(value, node, postcss4);
  }
  parent.push(node);
}
function parse2(obj, parent, postcss4) {
  var name, value, node, i;
  for (name in obj) {
    if (obj.hasOwnProperty(name)) {
      value = obj[name];
      if (value === null || typeof value === "undefined") {
        continue;
      } else if (name[0] === "@") {
        var parts = name.match(/@([^\s]+)(\s+([\w\W]*)\s*)?/);
        if (Array.isArray(value)) {
          for (i = 0; i < value.length; i++) {
            atRule(parent, parts, value[i], postcss4);
          }
        } else {
          atRule(parent, parts, value, postcss4);
        }
      } else if (Array.isArray(value)) {
        for (i = 0; i < value.length; i++) {
          decl(parent, name, value[i], postcss4);
        }
      } else if (typeof value === "object") {
        node = postcss4.rule({ selector: name });
        parse2(value, node, postcss4);
        parent.push(node);
      } else {
        decl(parent, name, value, postcss4);
      }
    }
  }
}
function cssObjToAst(obj, postcss4) {
  var root2 = postcss4.root();
  parse2(obj, root2, postcss4);
  return root2;
}

// src/codeActions/provideInvalidApplyCodeActions.ts
import { dset } from "dset";
import selectorParser from "postcss-selector-parser";
async function provideInvalidApplyCodeActions(state, document, diagnostic) {
  if (!document)
    return [];
  let documentText = getTextWithoutComments(document, "css");
  let cssRange;
  let cssText = documentText;
  const { postcss: postcss4 } = state.modules;
  let changes = [];
  let totalClassNamesInClassList = diagnostic.className.classList.classList.split(/\s+/).length;
  let className = diagnostic.className.className;
  let classNameParts = getClassNameParts(state, className);
  let classNameInfo = dlv10(state.classNames.classNames, classNameParts);
  if (Array.isArray(classNameInfo)) {
    return [];
  }
  if (!isCssDoc(state, document)) {
    let languageBoundaries = getLanguageBoundaries(state, document);
    if (!languageBoundaries)
      return [];
    cssRange = languageBoundaries.filter((b) => b.type === "css").find(({ range }) => isWithinRange(diagnostic.range.start, range))?.range;
    if (!cssRange)
      return [];
    cssText = getTextWithoutComments(document, "css", cssRange);
  }
  try {
    await postcss4.module([
      // TODO: use plain function?
      // @ts-ignore
      postcss4.module.plugin("", (_options = {}) => {
        return (root2) => {
          root2.walkRules((rule2) => {
            if (changes.length)
              return false;
            rule2.walkAtRules("apply", (atRule2) => {
              let atRuleRange = postcssSourceToRange(atRule2.source);
              if (cssRange) {
                atRuleRange = absoluteRange(atRuleRange, cssRange);
              }
              if (!isWithinRange(diagnostic.range.start, atRuleRange))
                return void 0;
              let ast = classNameToAst(
                state,
                classNameParts,
                rule2.selector,
                diagnostic.className.classList.important
              );
              if (!ast)
                return false;
              rule2.after(ast.nodes);
              let insertedRule = rule2.next();
              if (!insertedRule)
                return false;
              if (totalClassNamesInClassList === 1) {
                atRule2.remove();
              } else {
                changes.push({
                  range: diagnostic.className.classList.range,
                  newText: removeRangesFromString(
                    diagnostic.className.classList.classList,
                    diagnostic.className.relativeRange
                  )
                });
              }
              let ruleRange = postcssSourceToRange(rule2.source);
              if (cssRange) {
                ruleRange = absoluteRange(ruleRange, cssRange);
              }
              let outputIndent;
              let documentIndent = detectIndent(cssText);
              changes.push({
                range: ruleRange,
                newText: rule2.toString() + (insertedRule.raws.before || "\n\n") + insertedRule.toString().replace(/\n\s*\n/g, "\n").replace(/(@apply [^;\n]+)$/gm, "$1;").replace(/([^\s^]){$/gm, "$1 {").replace(/^\s+/gm, (m) => {
                  if (typeof outputIndent === "undefined")
                    outputIndent = m;
                  return m.replace(new RegExp(outputIndent, "g"), documentIndent.indent);
                }).replace(/^(\s+)(.*?[^{}]\n)([^\s}])/gm, "$1$2$1$3")
              });
              return false;
            });
            return void 0;
          });
        };
      })
    ]).process(cssText, { from: void 0 });
  } catch (_) {
    return [];
  }
  if (!changes.length) {
    return [];
  }
  return [
    {
      title: "Extract to new rule",
      kind: "quickfix",
      // CodeActionKind.QuickFix,
      diagnostics: [diagnostic],
      edit: {
        changes: {
          [document.uri]: changes
        }
      }
    }
  ];
}
function postcssSourceToRange(source) {
  return {
    start: {
      line: source.start.line - 1,
      character: source.start.column - 1
    },
    end: {
      line: source.end.line - 1,
      character: source.end.column
    }
  };
}
function classNameToAst(state, classNameParts, selector, important = false) {
  const baseClassName = classNameParts[classNameParts.length - 1];
  const validatedBaseClassName = validateApply(state, [baseClassName]);
  if (validatedBaseClassName === null || validatedBaseClassName.isApplyable === false) {
    return null;
  }
  const meta = getClassNameMeta(state, classNameParts);
  if (Array.isArray(meta))
    return null;
  let context = meta.context;
  let pseudo = meta.pseudo;
  const globalContexts = state.classNames.context;
  const path = [];
  for (let i = 0; i < classNameParts.length - 1; i++) {
    let part = classNameParts[i];
    let common = globalContexts[part];
    if (!common)
      return null;
    if (state.screens.includes(part)) {
      path.push(`@screen ${part}`);
      context = context.filter((con) => !common.includes(con));
    }
  }
  path.push(...context);
  let obj = {};
  for (let i = 1; i <= path.length; i++) {
    dset(obj, path.slice(0, i), {});
  }
  selector = appendPseudosToSelector(selector, pseudo);
  if (selector === null)
    return null;
  let rule2 = {
    [selector]: {
      [`@apply ${baseClassName}${important ? " !important" : ""}`]: ""
    }
  };
  if (path.length) {
    dset(obj, path, rule2);
  } else {
    obj = rule2;
  }
  return cssObjToAst(obj, state.modules.postcss);
}
function appendPseudosToSelector(selector, pseudos) {
  if (pseudos.length === 0)
    return selector;
  let canTransform = true;
  let transformedSelector = selectorParser((selectors) => {
    flatten(selectors.split((_) => true)).forEach((sel) => {
      for (let i = sel.nodes.length - 1; i >= 0; i--) {
        if (sel.nodes[i].type !== "pseudo") {
          break;
        } else if (pseudos.includes(sel.nodes[i].value)) {
          canTransform = false;
          break;
        }
      }
      if (canTransform) {
        pseudos.forEach((p) => {
          sel.append(selectorParser.pseudo({ value: p }));
        });
      }
    });
  }).processSync(selector);
  if (!canTransform)
    return null;
  return transformedSelector;
}

// src/codeActions/provideSuggestionCodeActions.ts
function provideSuggestionCodeActions(_state, params, diagnostic) {
  return diagnostic.suggestions.map((suggestion) => ({
    title: `Replace with '${suggestion}'`,
    kind: "quickfix",
    // CodeActionKind.QuickFix,
    diagnostics: [diagnostic],
    edit: {
      changes: {
        [params.textDocument.uri]: [
          {
            range: diagnostic.range,
            newText: suggestion
          }
        ]
      }
    }
  }));
}

// src/codeActions/codeActionProvider.ts
async function getDiagnosticsFromCodeActionParams(state, params, document, only) {
  if (!document)
    return [];
  let diagnostics = await doValidate(state, document, only);
  return params.context.diagnostics.map((diagnostic) => {
    return diagnostics.find((d) => {
      return d.code === diagnostic.code && d.message === diagnostic.message && rangesEqual(d.range, diagnostic.range);
    });
  }).filter(Boolean);
}
async function doCodeActions(state, params, document) {
  if (!state.enabled) {
    return [];
  }
  let diagnostics = await getDiagnosticsFromCodeActionParams(
    state,
    params,
    document,
    params.context.diagnostics.map((diagnostic) => diagnostic.code).filter(Boolean)
  );
  return Promise.all(
    diagnostics.map((diagnostic) => {
      if (isInvalidApplyDiagnostic(diagnostic)) {
        return provideInvalidApplyCodeActions(state, document, diagnostic);
      }
      if (isCssConflictDiagnostic(diagnostic)) {
        return provideCssConflictCodeActions(state, params, diagnostic);
      }
      if (isInvalidConfigPathDiagnostic(diagnostic) || isInvalidTailwindDirectiveDiagnostic(diagnostic) || isInvalidScreenDiagnostic(diagnostic) || isInvalidVariantDiagnostic(diagnostic) || isRecommendedVariantOrderDiagnostic(diagnostic)) {
        return provideSuggestionCodeActions(state, params, diagnostic);
      }
      return [];
    })
  ).then(flatten).then((x) => dedupeBy(x, (item) => JSON.stringify(item.edit)));
}

// src/documentColorProvider.ts
import dlv11 from "dlv";
async function getDocumentColors(state, document) {
  let colors = [];
  if (!state.enabled)
    return colors;
  let settings = await state.editor.getConfiguration(document.uri);
  if (settings.tailwindCSS.colorDecorators === false)
    return colors;
  let classLists = await findClassListsInDocument(state, document);
  classLists.forEach((classList) => {
    let classNames = getClassNamesInClassList(classList, state.blocklist);
    classNames.forEach((className) => {
      let color = getColor(state, className.className);
      if (color === null || typeof color === "string" || (color.alpha ?? 1) === 0) {
        return;
      }
      colors.push({
        range: className.range,
        color: culoriColorToVscodeColor(color)
      });
    });
  });
  let helperFns = findHelperFunctionsInDocument(state, document);
  helperFns.forEach((fn) => {
    let keys = stringToPath(fn.path);
    let base = fn.helper === "theme" ? ["theme"] : [];
    let value = dlv11(state.config, [...base, ...keys]);
    let color = getColorFromValue(value);
    if (color && typeof color !== "string" && (color.alpha ?? 1) !== 0) {
      colors.push({ range: fn.ranges.path, color: culoriColorToVscodeColor(color) });
    }
  });
  return dedupeByRange(colors);
}
export {
  DiagnosticKind,
  completionsFromClassList,
  culoriColorToVscodeColor,
  doCodeActions,
  doComplete2 as doComplete,
  doHover,
  doValidate,
  formatColor,
  getColor,
  getColorFromValue,
  getDocumentColors,
  isCssConflictDiagnostic,
  isInvalidApplyDiagnostic,
  isInvalidConfigPathDiagnostic,
  isInvalidScreenDiagnostic,
  isInvalidTailwindDirectiveDiagnostic,
  isInvalidVariantDiagnostic,
  isRecommendedVariantOrderDiagnostic,
  resolveCompletionItem
};
